/* Capsi quiz results (v2 design) — driven by the quiz's 27-pack engine.
 *
 * Reads the handoff assets/quiz.js stored (sessionStorage capsi_quiz_result,
 * falling back to localStorage capsi_quiz_result_v1). The handoff carries the
 * full quiz STATE under `.quiz`, including the recommendation the quiz already
 * computed: _goals (primary + secondary), _basePack, _upsells (base + pairs +
 * diet/activity nudges), _nudges, currentSupps (overlap is recalculated here
 * from the live selection, not read from _overlap), and the
 * melatonin decision. This page turns that into the v2 layout:
 *   - hero match card = the PRIMARY goal pack (pre-selected on the page; the
 *     Shopify cart is only written on a user action, never on load),
 *   - add-ons ("Complete your routine") = secondary goals + daily base + pairs
 *     + nudges (NOT pre-selected),
 *   - melatonin handling + "stop buying X separately" savings surfaced,
 *   - Ditto-style summary: Monthly (-10%) / 3-month (-25%) / one-time toggle.
 *
 * Pricing math is client-side from each pack's one-time base price (matching how
 * the Shopify subscription discounts are set: 10% monthly, 25% three-month).
 * Real price / variant / selling-plan / stock hydrate per shown pack from
 * /products/{handle}.js, matched through the pack_key→product map the template
 * emits. Until products carry custom.pack_key the page runs on placeholder prices.
 *
 * PACK_CATALOG editorial is grounded in Capsi_Pack_Ingredient_Reference.md (the
 * real OK Capsule formulations) and mirrors assets/pdp-packs.js — the ledger on
 * this page and the PDP pill list come from the same source. If formulations
 * change, regenerate pdp-packs.js and update the ledgers here together.
 */
(function () {
'use strict';

var root = document.querySelector('[data-results-root]');
if (!root) return;

/* Monochrome Navy motif art: currentColor is driven by the kit token on
   `.hero .face .art` (see quiz-results.css) — no pre-kit hexes, no Capsicum
   icons (P7). BG stays white for the small knockout masks. */
var INK = 'currentColor', CORAL = 'currentColor';
var CHECKOUT_URL = root.getAttribute('data-checkout-url') || '/cart';

/* ---------------- handoff ---------------- */
var data = null;
try { data = JSON.parse(sessionStorage.getItem('capsi_quiz_result') || 'null'); } catch (e) {}
if (!data || !data.quiz) { try { data = JSON.parse(localStorage.getItem('capsi_quiz_result_v1') || 'null'); } catch (e) {} }
if (!data || !data.quiz) {
  // Both storage reads failed (e.g. Safari private mode ran out of quota on
  // BOTH sessionStorage and localStorage on the quiz page) — quiz.js's last
  // resort is to carry the payload in the URL fragment. Decode it here, then
  // strip the hash so it's never bookmarked/shared with the payload attached.
  try {
    var hashMatch = /#r=([^&]+)/.exec(location.hash);
    if (hashMatch) {
      data = JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(hashMatch[1])))));
      try { history.replaceState(null, '', location.pathname + location.search); } catch (e) {}
    }
  } catch (e) {}
}

var resultView = root.querySelector('[data-result-view]');
var noResult = root.querySelector('[data-no-result]');
if (!data || !data.quiz || !(data.quiz._goals)) {
  if (noResult) noResult.hidden = false;
  return;
}
if (resultView) resultView.hidden = false;

var Q = data.quiz;
var firstName = (data.firstName || '').trim();

/* ---------------- pack name → canonical pack_key slug ---------------- */
var NAME2SLUG = {
  "Energy":"energy","Performance":"performance","Longevity":"longevity","Focus":"focus","Brain":"brain",
  "Sleep":"sleep","Stress":"stress","Mood Support":"mood","PMS":"pms","Perimenopause":"perimenopause",
  "Women's Hormone":"womens-hormone","Men's Hormone":"mens-hormone","Gut":"gut","Microbiome":"microbiome",
  "Clear Skin":"clear-skin","Beauty":"beauty","Hair":"hair","Joint Health":"joint","Bone Builder":"bone-builder",
  "Immune":"immune","Allergy":"allergy","Women's Wellness":"womens-wellness","Men's Wellness":"mens-wellness",
  /* "Daily Foundation" maps to immune: the pack was never built as a product, and
     older stored quiz results may still carry the name */
  "Vegan Nutrient":"vegan-nutrient","Daily Foundation":"immune","Antioxidant":"antioxidant","Mineral":"mineral"
};
function slugOf(name){ return NAME2SLUG[name] || String(name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,''); }

/* ---------------- 27-pack content catalog (real formulations) ----------------
   Ledgers list the actual pills + doses from Capsi_Pack_Ingredient_Reference.md
   (the OK Capsule formulations) — same source as assets/pdp-packs.js, so the
   results ledger always matches the PDP. The `sleep` slug is the melatonin-free
   product (pack_key `sleep` must stay on "Sleep Pack (no Melatonin)").
   price = one-time base price in dollars (overridden by real product price when
   a matching custom.pack_key product exists). motif = generated art style. */
var PACK_CATALOG = {
  "energy":{name:"Energy Pack",no:"No. 001",price:32,motif:"bars",
    why:"This pack pairs active B-vitamins with adaptogens that support cellular energy and the stress response, so steady output replaces the mid-day crash.",
    ledger:[["Methyl B-Complex","8 B vitamins"],["Rhodiola","250mg"],["Cordyceps","500mg"],["Ashwagandha","500mg"],["Tyrosine","500mg"],["Green Coffee Extract","500mg"]],
    tl:[["Weeks 1–2","Active B-vitamins and tyrosine support energy and alertness from week one."],["Weeks 3–4","Rhodiola and cordyceps steady the afternoon dip."],["Week 8+","A steadier baseline all day, without leaning on caffeine."]],
    rs:[["Low energy through the day","Methyl B-Complex"],["Afternoon energy dips","Rhodiola"],["Run-down lately","Cordyceps"]]},
  "performance":{name:"Performance Pack",no:"No. 002",price:38,motif:"grid",
    why:"This pack supports power output, endurance and recovery, so hard training translates into progress rather than fatigue.",
    ledger:[["Creatine","750mg"],["BCAA","600mg"],["Carnitine","500mg"],["Cordyceps","500mg"],["NR","500mg"],["Rhodiola","250mg"]],
    tl:[["Weeks 1–2","Creatine begins building stores; BCAAs support recovery between sessions."],["Weeks 3–4","Cordyceps and carnitine support endurance and output."],["Week 8+","Better output and faster bounce-back from hard training."]],
    rs:[["Train or work out regularly","Creatine"],["Slow recovery","BCAA"],["Endurance","Cordyceps"]]},
  "longevity":{name:"Longevity Pack",no:"No. 003",price:36,motif:"concentric",
    why:"This pack supports NAD+ levels, antioxidant defenses and cellular energy, the pathways that matter most for aging well.",
    ledger:[["NR","500mg"],["Resveratrol","250mg"],["CoQ10","200mg"],["NAC","600mg"],["Quercetin","500mg"],["Green Tea Extract","300mg"]],
    tl:[["Weeks 1–2","Antioxidant support from green tea, quercetin and NAC begins to build."],["Weeks 3–4","NR and CoQ10 support NAD+ and cellular energy."],["Week 8+","A protective baseline for healthy aging."]],
    rs:[["Aging well and protecting my cells","Resveratrol"],["Cellular energy","NR"],["Everyday protection","CoQ10"]]},
  "focus":{name:"Focus Pack",no:"No. 004",price:30,motif:"nodes",
    why:"This pack supports calm, sustained attention, so you stay sharp without the jitter or the crash.",
    ledger:[["Theanine","200mg"],["Lion's Mane","500mg"],["Tyrosine","500mg"],["Rhodiola","250mg"],["Magnesium","133mg"]],
    tl:[["Weeks 1–2","Theanine smooths attention from the first days."],["Weeks 3–4","Lion's Mane and tyrosine support clarity and processing."],["Week 8+","Sharper, calmer focus as the norm."]],
    rs:[["Focus and mental clarity","Theanine"],["Brain fog","Lion's Mane"],["Afternoon energy dips","Rhodiola"]]},
  "brain":{name:"Brain Pack",no:"No. 005",price:34,motif:"nodes",
    why:"This pack supports memory and long-term brain health, the structural side of staying sharp.",
    ledger:[["Lion's Mane","500mg"],["Ashwagandha","500mg"],["Theanine","200mg"],["Methyl B-Complex","8 B vitamins"],["Resveratrol","250mg"]],
    tl:[["Weeks 1–2","Theanine and active B-vitamins support a calm, clear baseline."],["Weeks 3–4","Lion's Mane's memory support builds."],["Week 8+","Better recall and long-term brain support."]],
    rs:[["Memory and long-term brain health","Lion's Mane"],["Foggy under stress","Ashwagandha"],["Mental clarity","Theanine"]]},
  "sleep":{name:"Sleep Pack",no:"No. 006",price:28,motif:"bars",
    why:"This pack supports calm onset and deep, restorative sleep. No melatonin, so you won't wake up groggy.",
    ledger:[["Herbal Sleep Blend","450mg"],["Magnesium","133mg"],["Theanine","200mg"]],
    tl:[["Weeks 1–2","Magnesium and theanine settle the evenings."],["Weeks 3–4","The five-herb sleep blend deepens rest; fewer 3am wake-ups."],["Week 8+","A consolidated rhythm and rested mornings."]],
    rs:[["Trouble sleeping","Magnesium"],["Waking through the night","Herbal Sleep Blend"],["Wired at bedtime","Theanine"]]},
  "stress":{name:"Stress Pack",no:"No. 007",price:30,motif:"geo",
    why:"This pack supports the stress response, calm focus and deeper rest, the areas a heavy stress load tends to deplete first.",
    ledger:[["Ashwagandha","500mg"],["Theanine","200mg"],["Magnesium","133mg"],["Methyl B-Complex","8 B vitamins"],["Omega 3","1000mg"],["Cordyceps","500mg"]],
    tl:[["Weeks 1–2","Magnesium and theanine settle the evenings."],["Weeks 3–4","Ashwagandha's adaptogenic effect builds."],["Week 8+","Steadier energy, deeper sleep."]],
    rs:[["Wired or tense","Ashwagandha + Theanine"],["Trouble sleeping","Magnesium"],["Afternoon energy dips","Cordyceps"]]},
  "mood":{name:"Mood Support Pack",no:"No. 008",price:30,motif:"concentric",
    why:"This pack supports a steadier, brighter mood and a calmer baseline through low or anxious stretches.",
    ledger:[["Ashwagandha","500mg"],["Rhodiola","250mg"],["Theanine","200mg"],["Magnesium","133mg"],["Methyl B-Complex","8 B vitamins"],["Zinc","25mg"]],
    tl:[["Weeks 1–2","Magnesium and theanine ease tension."],["Weeks 3–4","Ashwagandha and rhodiola steady the stress response."],["Week 8+","A steadier, brighter baseline."]],
    rs:[["Low or anxious mood","Ashwagandha"],["Wired or tense","Theanine"],["Flat lately","Methyl B-Complex"]]},
  "pms":{name:"PMS Pack",no:"No. 009",price:24,motif:"concentric",
    why:"This pack targets the cramps, tension and mood shifts that build in the days before your period.",
    ledger:[["Magnesium","133mg"],["DIM","300mg"],["Calcium Citrate","160mg"],["Ashwagandha","500mg"],["Methyl B-Complex","8 B vitamins"],["Omega 3","1000mg"]],
    tl:[["Cycle 1","Magnesium and calcium ease cramps and tension."],["Cycle 2","DIM supports estrogen metabolism through the luteal phase."],["Cycle 3+","Calmer, more predictable pre-period days."]],
    rs:[["Cramps before my period","Magnesium"],["Mood swings","Ashwagandha"],["Hormone balance","DIM"]]},
  "perimenopause":{name:"Perimenopause Pack",no:"No. 010",price:38,motif:"concentric",
    why:"This pack supports hormone balance, energy and calm through the shifts of perimenopause.",
    ledger:[["DIM","300mg"],["DHEA","25mg"],["Fenugreek","500mg"],["Magnesium","133mg"],["Cordyceps","500mg"]],
    tl:[["Weeks 1–2","Magnesium settles sleep and tension."],["Weeks 3–4","DIM and DHEA support hormonal balance."],["Week 8+","Steadier days and nights through the transition."]],
    rs:[["Hormonal shifts","DIM"],["Energy dips","Cordyceps"],["Tension and sleep","Magnesium"]]},
  "womens-hormone":{name:"Women's Hormone Pack",no:"No. 011",price:30,motif:"concentric",
    why:"This pack supports overall hormonal balance, mood and libido across your cycle.",
    ledger:[["DIM","300mg"],["DHEA","25mg"],["Fenugreek","500mg"],["Cordyceps","500mg"],["Omega 3","1000mg"]],
    tl:[["Cycle 1","Omega-3s and cordyceps steady energy."],["Cycle 2","DIM and DHEA support hormonal balance."],["Cycle 3+","Steadier mood, energy and libido."]],
    rs:[["Balance, mood and libido","DHEA"],["Low drive","Fenugreek"],["Estrogen metabolism","DIM"]]},
  "mens-hormone":{name:"Men's Hormone Pack",no:"No. 012",price:34,motif:"geo",
    why:"This pack supports healthy testosterone, energy, libido and strength.",
    ledger:[["Tribulus","500mg"],["Fenugreek","500mg"],["DHEA","25mg"],["DIM","300mg"],["Zinc","25mg"],["Boron","10mg"]],
    tl:[["Weeks 1–2","Zinc and boron support the hormonal baseline."],["Weeks 3–4","Fenugreek and tribulus build."],["Week 8+","Steadier energy, libido and strength."]],
    rs:[["Energy, libido and strength","Tribulus"],["Low drive","Fenugreek"],["Testosterone support","Zinc"]]},
  "gut":{name:"Gut Pack",no:"No. 013",price:26,motif:"grid",
    why:"This pack settles bloating and post-meal discomfort while supporting the gut lining.",
    ledger:[["10B Probiotic","10B CFU"],["Soothing Fiber Formula","350mg"],["Triphala","500mg"],["Glutamine","750mg"],["Quercetin","500mg"]],
    tl:[["Weeks 1–2","The aloe, marshmallow and slippery elm fiber blend soothes digestion."],["Weeks 3–4","Probiotics shift the balance; glutamine supports the gut lining."],["Week 8+","Calmer digestion and less daily discomfort."]],
    rs:[["Bloating and discomfort","Soothing Fiber Formula"],["Irregular digestion","Triphala"],["Gut lining","Glutamine"]]},
  "microbiome":{name:"Microbiome Pack",no:"No. 014",price:34,motif:"cross",
    why:"This pack builds a resilient, diverse microbiome for better overall gut health.",
    ledger:[["10B Probiotic","10B CFU"],["Berberine","300mg"],["Soothing Fiber Formula","350mg"],["Turmeric","700mg"],["Quercetin","500mg"],["Resveratrol","250mg"]],
    tl:[["Weeks 1–2","Soluble fiber soothes and feeds existing flora."],["Weeks 3–4","The multi-strain probiotic establishes; berberine supports metabolic balance."],["Week 8+","A more diverse, resilient microbiome."]],
    rs:[["Better overall gut health","10B Probiotic"],["Metabolic support","Berberine"],["Balanced gut tone","Turmeric"]]},
  "clear-skin":{name:"Clear Skin Pack",no:"No. 015",price:28,motif:"nodes",
    why:"This pack works the skin-gut axis to support clearer skin and fewer breakouts.",
    ledger:[["Zinc","25mg"],["10B Probiotic","10B CFU"],["NAC","600mg"],["Turmeric","700mg"],["Vitamin C","1000mg"],["Omega 3","1000mg"]],
    tl:[["Weeks 1–2","Zinc and vitamin C support skin repair."],["Weeks 3–6","The probiotic works the skin-gut axis; breakouts begin to settle."],["Week 8+","Clearer, calmer skin."]],
    rs:[["Clearer skin and fewer breakouts","Zinc"],["Skin-gut axis","10B Probiotic"],["Skin repair","Vitamin C"]]},
  "beauty":{name:"Beauty Pack",no:"No. 016",price:34,motif:"concentric",
    why:"This pack supports skin hydration and elasticity plus hair and nail strength, from the inside.",
    ledger:[["Hyaluronic Acid","60mg"],["Biotin","5000mcg"],["Omega 3","1000mg"],["Quercetin","500mg"],["Resveratrol","250mg"],["D Complex","5,000 IU D3 · A/E/K2"]],
    tl:[["Weeks 1–4","Biotin and omega-3s lay the base for hair, skin and nails."],["Weeks 4–8","Hyaluronic acid supports skin hydration and elasticity."],["Week 12+","Visibly smoother tone and stronger hair and nails."]],
    rs:[["Fine lines and dullness","Hyaluronic Acid"],["Hair and nails","Biotin"],["Skin hydration","Omega 3"]]},
  "hair":{name:"Hair Pack",no:"No. 017",price:32,motif:"bars",
    why:"This pack supports the follicle through the growth cycle to reduce shedding and thinning.",
    ledger:[["Saw Palmetto","650mg"],["Biotin","5000mcg"],["Hyaluronic Acid","60mg"],["Omega 3","1000mg"],["Resveratrol","250mg"],["Trace Minerals","11 trace elements"]],
    tl:[["Weeks 1–4","Follicle support begins; shedding may settle."],["Weeks 8–12","New growth strengthens at the root."],["Month 4+","Fuller, more resilient hair."]],
    rs:[["Thinning or shedding hair","Saw Palmetto"],["Strength at the root","Biotin"],["Scalp and strand health","Omega 3"]]},
  "joint":{name:"Joint Health Pack",no:"No. 018",price:32,motif:"grid",
    why:"This pack supports cartilage, cushioning and everyday comfort for lasting joint mobility.",
    ledger:[["Vegan Glucosamine","1000mg"],["Hyaluronic Acid","60mg"],["Omega 3","1000mg"],["Vitamin C","1000mg"],["Boron","10mg"],["Trace Minerals","11 trace elements"]],
    tl:[["Weeks 1–2","Omega-3s support day-to-day comfort."],["Weeks 4–8","Glucosamine and hyaluronic acid support cartilage and cushioning."],["Week 12+","Lasting comfort and mobility."]],
    rs:[["Joint aches and stiffness","Vegan Glucosamine"],["Cushioning and mobility","Hyaluronic Acid"],["Collagen production","Vitamin C"]]},
  "bone-builder":{name:"Bone Builder Pack",no:"No. 019",price:30,motif:"grid",
    why:"This pack supports long-term bone strength, getting calcium where it belongs.",
    ledger:[["Calcium Citrate","160mg"],["D Complex","5,000 IU D3 · A/E/K2"],["Magnesium","133mg"],["Hyaluronic Acid","60mg"],["Trace Minerals","11 trace elements"],["10B Probiotic","10B CFU"]],
    tl:[["Weeks 1–4","Vitamin D3 supports calcium absorption."],["Months 2–3","K2 in the D Complex helps direct calcium to bone."],["Month 6+","Stronger, better-supported bone."]],
    rs:[["Bone strength","Calcium Citrate"],["Absorption","D Complex"],["Muscle and bone support","Magnesium"]]},
  "immune":{name:"Immune Pack",no:"No. 020",price:26,motif:"cross",
    why:"This pack fortifies first-line defenses so you hold up through the season and bounce back faster.",
    ledger:[["Vitamin C","1000mg"],["Zinc","25mg"],["Mushroom Immune","525mg"],["D Complex","5,000 IU D3 · A/E/K2"],["10B Probiotic","10B CFU"]],
    tl:[["Weeks 1–2","Zinc and vitamin C support first-line defense."],["Weeks 3–4","The 14-mushroom blend adds adaptogenic immune support."],["Week 8+","More resilience and faster bounce-back."]],
    rs:[["I catch colds and bugs easily","Zinc"],["Run-down","Vitamin C"],["Immune resilience","Mushroom Immune"]]},
  "allergy":{name:"Allergy Pack",no:"No. 021",price:26,motif:"cross",
    why:"This pack supports a balanced histamine response through allergy season.",
    ledger:[["Quercetin","500mg"],["NAC","600mg"],["Vitamin C","1000mg"],["Omega 3","1000mg"],["D Complex","5,000 IU D3 · A/E/K2"],["Trace Minerals","11 trace elements"]],
    tl:[["Week 1","Quercetin and vitamin C begin to steady the response."],["Weeks 2–3","NAC supports healthy respiratory tissue."],["Season-long","A steadier response through allergy season."]],
    rs:[["Seasonal allergies","Quercetin"],["Respiratory comfort","NAC"],["Everyday defense","Vitamin C"]]},
  "womens-wellness":{name:"Women's Wellness Pack",no:"No. 022",price:29,motif:"geo",
    why:"Your everyday base. It covers the gaps a typical diet leaves, tuned for women.",
    ledger:[["Multivitamin","daily tablet"],["D Complex","5,000 IU D3 · A/E/K2"],["Omega 3","1000mg"],["Vitamin C","1000mg"],["Ashwagandha","500mg"],["10B Probiotic","10B CFU"]],
    tl:[["Weeks 1–2","Fills the most common daily gaps."],["Weeks 3–4","Vitamin D and omega-3 status improve."],["Week 8+","A covered, steady nutritional baseline."]],
    rs:[["Cover the gaps in my diet","Multivitamin"],["Less winter sun","D Complex"],["Rarely eat fish","Omega 3"]]},
  "mens-wellness":{name:"Men's Wellness Pack",no:"No. 023",price:29,motif:"geo",
    why:"Your everyday base. It covers the gaps a typical diet leaves, tuned for men.",
    ledger:[["Multivitamin","daily tablet"],["D Complex","5,000 IU D3 · A/E/K2"],["Omega 3","1000mg"],["Methyl B-Complex","8 B vitamins"],["CoQ10","200mg"],["Ashwagandha","500mg"],["10B Probiotic","10B CFU"]],
    tl:[["Weeks 1–2","Fills the most common daily gaps."],["Weeks 3–4","Vitamin D and omega-3 status improve."],["Week 8+","A covered, steady nutritional baseline."]],
    rs:[["Cover the gaps in my diet","Multivitamin"],["Less winter sun","D Complex"],["Rarely eat fish","Omega 3"]]},
  "vegan-nutrient":{name:"Vegan Nutrient Pack",no:"No. 024",price:30,motif:"nodes",
    why:"Covers the nutrients a plant-based diet most often runs low on: B12, D, iron, omega-3s and zinc.",
    ledger:[["Methyl B-Complex","8 B vitamins"],["Iron","18mg"],["Vegan Omega","275mg"],["D Complex","5,000 IU D3 · A/E/K2"],["Calcium Citrate","160mg"],["Zinc","25mg"],["Trace Minerals","11 trace elements"]],
    tl:[["Weeks 1–2","B12 and iron begin to top up."],["Weeks 3–4","Vitamin D and omega-3 status improve."],["Week 8+","The common plant-based gaps, covered."]],
    rs:[["Plant-based diet","Methyl B-Complex"],["Iron without red meat","Iron"],["Omega-3 without fish","Vegan Omega"]]},
  "daily-foundation":{name:"Daily Foundation Pack",no:"No. 025",price:29,motif:"geo",
    why:"Your everyday base. It covers the gaps in a typical diet, the dependable bottom layer of any routine.",
    ledger:[["Multivitamin","daily tablet"],["D Complex","5,000 IU D3 · A/E/K2"],["Omega 3","1000mg"],["10B Probiotic","10B CFU"]],
    tl:[["Weeks 1–2","Fills the most common daily gaps."],["Weeks 3–4","Vitamin D and omega-3 status improve."],["Week 8+","A covered, steady nutritional baseline."]],
    rs:[["Cover the gaps in my diet","Multivitamin"],["Less winter sun","D Complex"],["Rarely eat fish","Omega 3"]]},
  "antioxidant":{name:"Antioxidant Pack",no:"No. 026",price:22,motif:"concentric",
    why:"This pack helps combat free-radical load to support healthy aging, recovery and everyday resilience.",
    ledger:[["Alpha Lipoic Acid","500mg"],["NAC","600mg"],["Quercetin","500mg"],["Resveratrol","250mg"],["Vitamin C","1000mg"],["Trace Minerals","11 trace elements"]],
    tl:[["Weeks 1–2","Vitamin C and quercetin lift antioxidant capacity."],["Weeks 3–4","NAC supports glutathione, the master antioxidant."],["Week 8+","Better recovery and everyday resilience."]],
    rs:[["Protecting my cells","Alpha Lipoic Acid"],["Recovery","NAC"],["Healthy aging","Resveratrol"]]},
  "mineral":{name:"Mineral Pack",no:"No. 027",price:18,motif:"grid",
    why:"This pack fills the mineral gaps most diets miss, the quiet deficiencies behind low energy and poor sleep.",
    ledger:[["Magnesium","133mg"],["Calcium Citrate","160mg"],["Boron","10mg"],["Trace Minerals","11 trace elements"]],
    tl:[["Weeks 1–2","Magnesium supports sleep and calm."],["Weeks 3–4","Calcium and trace elements top up."],["Week 8+","The common mineral gaps, filled."]],
    rs:[["Rarely eat leafy greens","Magnesium"],["Bone and hormone support","Boron"],["Trace element gaps","Trace Minerals"]]}
};
function pack(slug){ return PACK_CATALOG[slug] || {name:slug,no:"",price:0,motif:"geo",why:"",ledger:[],tl:[],rs:[]}; }
/* Ingredient forms verified against the supplier records (OK Capsule audit,
   Capsi-Project-Docs/OKCapsule_MCP_Enumeration.md). Only these show; nothing is
   inferred. Methyl B-Complex and Calcium Citrate already carry their form. */
var FORMS = { "Magnesium":"glycinate", "Iron":"Ferrochel®", "Vegan Omega":"algal oil" };
/* ---------------- manufacturer ingredient records ----------------
   Pulled 2026-09-27 from OK Capsule's product-intelligence records (one per
   ingredient SKU; pack-level records aren't retrievable). `form` is the
   supplier's stated active form, trimmed for display. `no` is the supplier's
   `contains_no` list, normalised (Milk→dairy, Fish/Shellfish→both, Nuts→
   peanuts + tree nuts). A pack only claims "free from X" when EVERY ingredient
   in it lists X, so the line is never broader than the records. `plant` = the
   record lists "Animal Products" as absent. Re-pull if formulations change. */
var ING_FACTS = {
  "Magnesium":{form:"glycinate", no:"d w s p t e sh se f"},
  "DIM":{form:"diindolylmethane", no:"d w s p t e sh se f"},
  "DHEA":{form:"micronized", no:"d g w s p t e sh se f", plant:1},
  "Fenugreek":{form:"seed extract, 50% saponins", no:"d w s p t e sh se f"},
  "Cordyceps":{form:"organic", no:"d g w s p t e sh se f", plant:1},
  "Ashwagandha":{form:"organic root + extract, 1.5% withanolides", no:"d g w s p t e sh se f"},
  "Rhodiola":{form:"root extract, standardized rosavins", no:"d g w s p t e sh se f"},
  "Methyl B-Complex":{form:"methylcobalamin, methylfolate, P-5-P", no:"s p t e sh se f d"},
  "Theanine":{form:"L-theanine", no:"w s p t e sh se f d", plant:1},
  "Omega 3":{form:"fish oil, 400mg EPA + 300mg DHA, molecularly distilled", no:"d g w s p t e sh se"},
  "Zinc":{form:"picolinate", no:"w s p t e sh se f d", plant:1},
  "D Complex":{form:"D3 5,000 IU with K2 (MK-4 + MK-7), A and E", no:"d w s p t e sh se f"},
  "Vitamin C":{form:"ascorbic acid", no:"d w s p t e sh se f"},
  "10B Probiotic":{form:"10 strains + prebiotic fiber", no:"w s p t e sh se f d", plant:1},
  "Vegan Glucosamine":{form:"GreenGrown®, plant-based", no:"d g w s p t e sh se f", plant:1},
  "Hyaluronic Acid":{form:"from chicken type II collagen", no:"d g w s p t e sh se f"},
  "Calcium Citrate":{form:"citrate", no:"d w s p t e sh se f"},
  "Multivitamin":{form:"Albion® chelated minerals, methylfolate, K1 + K2", no:"w s p t e sh se f d"},
  "Iron":{form:"Ferrochel® bisglycinate", no:"w s p t e sh se f d", plant:1},
  "Vegan Omega":{form:"algal oil, 150mg DHA + 75mg EPA", no:"d g w s p t e sh se f", plant:1},
  "Trace Minerals":{form:"Albion® chelates, iron-free", no:"d w s p t e sh se f"},
  "Boron":{form:"glycinate", no:"d g w s p t e sh se f", plant:1},
  "Herbal Sleep Blend":{form:"valerian, hops, passionflower, skullcap, poppy", no:"d w s p t e sh se f"},
  "Biotin":{form:"", no:"d g w s p t e sh se f", plant:1},
  "Mushroom Immune":{form:"14 organically grown mushrooms", no:"d g w s p t se", plant:1},
  "Tyrosine":{form:"L-tyrosine", no:"d g w s p t e sh se f", plant:1},
  "Green Coffee Extract":{form:"50% chlorogenic acid", no:"d g w s p t e sh se f"},
  "Creatine":{form:"monohydrate", no:"d w s p t e sh se f"},
  "BCAA":{form:"2:1:1 leucine, isoleucine, valine", no:"d w s p t e sh se f"},
  "Carnitine":{form:"L-carnitine tartrate", no:"d w s p t e sh se f"},
  "NR":{form:"nicotinamide riboside", no:"d w s p t e sh se f"},
  "Resveratrol":{form:"trans-resveratrol, Japanese knotweed", no:"d g w s p t e sh se f", plant:1},
  "CoQ10":{form:"ubiquinone", no:"w s p t e sh se f d", plant:1},
  "NAC":{form:"N-acetyl cysteine", no:"d w s p t e sh se f"},
  "Quercetin":{form:"", no:"d w s p t e sh se f"},
  "Green Tea Extract":{form:"95% polyphenols, 50% EGCG", no:"d w s p t e sh se f"},
  "Lion's Mane":{form:"organic, double-extracted", no:"d g s p t sh se f", plant:1},
  "Tribulus":{form:"fruit extract, 75% saponins", no:"d w s p t e sh se f"},
  "Soothing Fiber Formula":{form:"organic aloe, marshmallow root, slippery elm", no:"d g w s p t sh se f"},
  "Triphala":{form:"organic", no:"d g w s p t sh se f"},
  "Glutamine":{form:"L-glutamine", no:"d w s p t e sh se f"},
  "Berberine":{form:"Berberis aristata root extract", no:"d w s p t e sh se f"},
  "Turmeric":{form:"Curcumin C3 Complex® + BioPerine®", no:"d w s p t e sh se f", plant:1},
  "Saw Palmetto":{form:"45% fatty acids, hexane-free", no:"d g w s p t e sh se f", plant:1},
  "Alpha Lipoic Acid":{form:"", no:"w s p t e sh se f d", plant:1},
  "Melatonin SR":{form:"sustained-release", no:"d w s p t e sh se f"}
};
/* "Before you start": plain-English versions of the manufacturer's own warnings */
var ING_WARN = {
  "DHEA":"DHEA is a hormone precursor. Don't take it if you're pregnant or nursing, and check with your doctor first if you use hormonal birth control or have a personal or family history of breast or prostate cancer or heart disease.",
  "DIM":"Don't take DIM if you're pregnant or nursing.",
  "D Complex":"It contains vitamin K, which can interact with blood thinners.",
  "Herbal Sleep Blend":"Don't drive or operate machinery after taking it.",
  "Melatonin SR":"Don't drive or operate machinery after taking it.",
  "Turmeric":"Don't take turmeric if you're pregnant or nursing.",
  "Saw Palmetto":"Don't take saw palmetto if you're pregnant or nursing.",
  "Soothing Fiber Formula":"Don't take the aloe fiber blend if you're pregnant or nursing.",
  "Iron":"Adults only. Don't take more than one packet a day."
};
var ALLERGEN_WORD = {d:"dairy", g:"gluten", w:"wheat", s:"soy", p:"peanuts", t:"tree nuts", e:"eggs", f:"fish", sh:"shellfish", se:"sesame"};
var ALLERGEN_ORDER = ["d","g","w","s","p","t","e","f","sh","se"];
/* allergens absent from EVERY ingredient in the pack (per the supplier records) */
function packFreeFrom(slug){
  var led=pack(slug).ledger.map(function(r){return r[0];});
  if(!led.length || led.some(function(n){ return !ING_FACTS[n]; })) return [];
  return ALLERGEN_ORDER.filter(function(a){ return led.every(function(n){ return (' '+ING_FACTS[n].no+' ').indexOf(' '+a+' ')>=0; }); });
}
function packPlantBased(slug){
  var led=pack(slug).ledger.map(function(r){return r[0];});
  return led.length>0 && led.every(function(n){ return ING_FACTS[n] && ING_FACTS[n].plant; });
}

/* ---------------- first-order offer (B only) ----------------
   Set on the template root: data-first-order-code / data-first-order-pct.
   The code is a Shopify discount (first order, one per customer, one-time +
   subscription first payment) that stacks on the selling-plan price. It is
   shown here and applied by sending checkout to /checkout?discount=CODE.
   Remove the two attributes to switch the offer off. */
var FO_CODE = (root.getAttribute('data-first-order-code')||'').trim();
var FO_PCT  = parseFloat(root.getAttribute('data-first-order-pct')||'0') || 0;
function foOn(){ return !!(FO_CODE && FO_PCT>0); }
/* subscriptions only (Jack, 2026-09-27): the one-time plan never gets the first-order discount */
function foFor(k){ return foOn() && k!=='once'; }

/* ---------------- real product hydration (slug → product) ----------------
   The template emits every product in the "packs" collection. Resolve each to a
   pack slug by custom.pack_key when set, otherwise by matching the product title
   to a catalog pack name — so the page hydrates real prices even before any
   pack_key metafields are wired (products already ship titled e.g. "Focus Pack"). */
var CATALOG_NAME2SLUG = {};
Object.keys(PACK_CATALOG).forEach(function(slug){
  var nm = PACK_CATALOG[slug].name.toLowerCase().trim();
  CATALOG_NAME2SLUG[nm] = slug;                          // "focus pack"
  CATALOG_NAME2SLUG[nm.replace(/\s*pack$/,'').trim()] = slug; // "focus"
});
function titleToSlug(title){
  var t = String(title||'').toLowerCase().trim();
  return CATALOG_NAME2SLUG[t] || CATALOG_NAME2SLUG[t.replace(/\s*pack$/,'').trim()] || null;
}
var PRODUCTS = {};
try {
  var feed = JSON.parse(document.getElementById('capsi-pack-products').textContent || '[]') || [];
  feed = Array.isArray(feed) ? feed : [];
  /* pass 1: products with an explicit pack_key win (e.g. the keyed "Sleep Pack
     (no Melatonin)" must own the `sleep` slug over the plain melatonin "Sleep Pack") */
  feed.forEach(function(p){ if (p.pack_key && PACK_CATALOG[p.pack_key]) PRODUCTS[p.pack_key] = p; });
  /* pass 2: fill any remaining slug by matching the product title to a catalog name */
  feed.forEach(function(p){ var slug = titleToSlug(p.title); if (slug && !PRODUCTS[slug]) PRODUCTS[slug] = p; });
} catch (e) {}
var productCache = {}; /* handle → fetched /products/{handle}.js */

/* ---------------- committed profiles (sequential multi-profile order) ----------------
   Each person already "added" to the order is committed to the live Shopify cart
   and remembered here (name + pack count, for display), so the next person's
   results page can show who's already sharing the order. The cart is the source
   of truth; this list is just the names/counts for display. */
var PROFILES_KEY = 'capsi_quiz_profiles';
var committedCount = 0, committedTotal = 0; /* from /cart.js — the real shared cart */
function getProfiles(){ try{ var l=JSON.parse(localStorage.getItem(PROFILES_KEY)||'[]'); return Array.isArray(l)?l:[]; }catch(e){ return []; } }
function setProfiles(l){ try{ localStorage.setItem(PROFILES_KEY, JSON.stringify(l)); }catch(e){} }
function loadCommitted(){
  restoreSessionLines();                       /* what THIS session already put in the cart (survives refresh) */
  return fetch('/cart.js',{headers:{'X-Requested-With':'XMLHttpRequest'}})
    .then(function(r){ return r.ok?r.json():null; })
    .then(function(c){
      var items=(c&&c.items)||[];
      var totalCount=c?(c.item_count||0):0;
      var totalCents=c?(c.total_price||0):0;
      var byKey={}; items.forEach(function(it){ byKey[it.key]=it; });

      /* cart emptied (e.g. the order was placed) → everything we remembered is stale */
      if(totalCount===0){
        ourLineBySlug={}; saveSessionLines();
        try{ localStorage.removeItem(PROFILES_KEY); }catch(e){}
        try{ localStorage.removeItem(PLAN_KEY); }catch(e){}
        try{ localStorage.removeItem(ACTIVE_PLAN_KEY); }catch(e){}
        committedCount=0; committedTotal=0; baselineQtyByKey={}; baselineSnapped=true;
        return;
      }

      /* drop remembered lines that are no longer in the cart (stale keys) */
      Object.keys(ourLineBySlug).forEach(function(s){ if(!byKey[ourLineBySlug[s].key]) delete ourLineBySlug[s]; });
      saveSessionLines();

      /* baseline = the lines in the cart MINUS our own, so removeOurLine floors to
         the earlier-profile quantity (not ours) and we never double-count ourselves */
      baselineQtyByKey={};
      items.forEach(function(it){ baselineQtyByKey[it.key]=(baselineQtyByKey[it.key]||0)+it.quantity; });
      var ourCount=0, ourCents=0;
      Object.keys(ourLineBySlug).forEach(function(s){
        var it=byKey[ourLineBySlug[s].key]; if(!it) return;
        ourCount+=1; ourCents+=(it.final_price!=null?it.final_price:it.price);
        if(baselineQtyByKey[it.key]){ baselineQtyByKey[it.key]-=1; if(baselineQtyByKey[it.key]<=0) delete baselineQtyByKey[it.key]; }
      });
      baselineSnapped=true;

      /* committed = earlier profiles only (whole cart minus our own lines) */
      committedCount=Math.max(0, totalCount-ourCount);
      committedTotal=Math.max(0, (totalCents-ourCents)/100);
      if(committedCount>0){
        /* inherit + lock the cadence the earlier profiles were committed on */
        try{ var sp=localStorage.getItem(PLAN_KEY); if(sp){ plan=sp; orderPlanLocked=true; } }catch(e){}
      } else {
        /* no earlier profiles → clear any stale committed-order markers */
        try{ localStorage.removeItem(PROFILES_KEY); }catch(e){}
        try{ localStorage.removeItem(PLAN_KEY); }catch(e){}
      }
    })
    .catch(function(){ committedCount=0; committedTotal=0; baselineQtyByKey={}; baselineSnapped=true; });
}

/* base one-time price (dollars): real product price when keyed, else placeholder */
function basePrice(slug){
  var p = PRODUCTS[slug];
  if (p && typeof p.price === 'number' && p.price > 0) return p.price / 100;
  return pack(slug).price;
}

/* ---------------- recommendation: hero + add-ons ---------------- */
var goals = Q._goals || [];
var primaryGoal = goals.find(function(g){return g.role==='primary';}) || goals[0];
var heroSlug = slugOf(primaryGoal ? primaryGoal.pack : (Q._basePack || 'Daily Foundation'));
var baseSlug = slugOf(Q._basePack || 'Daily Foundation');
/* The assessment's concern options (leavesFor in assets/quiz.js), keyed by the pack
   each maps to. Every concern maps to exactly one pack, so this recovers the words
   the buyer actually tapped for their headline pack. Keep in step with leavesFor. */
var CONCERN_LABELS = {
  "Energy":"Low energy through the day","Sleep":"Trouble sleeping","Stress":"Feeling wired or tense",
  "Focus":"Focus and mental clarity","Mood Support":"Low or anxious mood","Longevity":"Aging well and protecting my cells",
  "Gut":"Bloating and discomfort","Microbiome":"Better overall gut health","Immune":"I catch colds and bugs easily",
  "Joint Health":"Joint aches and stiffness","Allergy":"Seasonal allergies","Bone Builder":"Bone strength",
  "Men's Hormone":"Energy, libido and strength","PMS":"Cramps, mood and bloating before my period",
  "Women's Hormone":"Overall balance, mood and libido","Perimenopause":"Hot flashes and changing cycles",
  "Clear Skin":"Clearer skin and fewer breakouts","Hair":"Thinning or shedding hair","Beauty":"Fine lines and aging"
};
/* the concern behind the hero pack, or '' when the hero wasn't a concern they picked
   (the daily-base fallback, or a "just a strong daily foundation" pick) */
function pickedConcernLabel(){
  var name = primaryGoal && primaryGoal.pack;
  if(!name || !CONCERN_LABELS[name]) return '';
  var picked = (Q.goals||[]).some(function(g){ return g && g.pack===name; });
  return picked ? CONCERN_LABELS[name] : '';
}
var secondarySlugs = goals.filter(function(g){return g!==primaryGoal;}).map(function(g){return slugOf(g.pack);});
var nudgeSlugs = Object.keys(Q._nudges || {}).map(slugOf);
var nudgeReasonBySlug = {};
Object.keys(Q._nudges || {}).forEach(function(name){ nudgeReasonBySlug[slugOf(name)] = Q._nudges[name]; });

/* goals the user picked that ranked just below the top three (v2 GOAL_CAP). These
   are the user's OWN picks, not something we suggested, so they stay shoppable and
   are never trimmed — surfacing them keeps the engine's "present the 3 best,
   nothing silently dropped" contract on the results page. */
var alsoConsideredSlugs = [];
var alsoReasonBySlug = {};
(Q._alsoConsidered || []).forEach(function(a){
  var s = slugOf(a.pack);
  if (s && PACK_CATALOG[s]) { if (alsoConsideredSlugs.indexOf(s)<0) alsoConsideredSlugs.push(s); alsoReasonBySlug[s] = a.reason; }
});

/* ordered add-on list: secondary goals → daily base → pairs/nudges (upsells) →
   also-picked goals; deduped, hero excluded */
var addonSlugs = [];
function pushAddon(s){ if (s && s!==heroSlug && PACK_CATALOG[s] && addonSlugs.indexOf(s)<0) addonSlugs.push(s); }
secondarySlugs.forEach(pushAddon);
pushAddon(baseSlug);
(Q._upsells || []).forEach(function(name){ pushAddon(slugOf(name)); });
alsoConsideredSlugs.forEach(pushAddon);
/* Keep "Complete your routine" curated: the user's own goals (secondary + also-
   picked) and the daily base always stay, but cap the extras WE suggest
   (diet/activity nudges + pairs) so the list doesn't balloon. The rest is still
   one tap away via "Shop the full collection". */
var MAX_EXTRA_RECS = 0;   /* 2026-09-26: no inferred nudges; add-ons are the buyer's own picks + the daily base */
(function(){
  var kept=[], extras=[];
  addonSlugs.forEach(function(s){
    if (s===baseSlug || secondarySlugs.indexOf(s)>=0 || alsoConsideredSlugs.indexOf(s)>=0) kept.push(s); else extras.push(s);
  });
  addonSlugs = kept.concat(extras.slice(0, MAX_EXTRA_RECS));
  /* someone who takes nothing today: don't show a wall of packs */
  if (Q.q1==='none' && !(Q.currentSupps||[]).length) addonSlugs = addonSlugs.slice(0, 2);
})();

function roleLabel(slug){
  if (slug===baseSlug) return 'Everyday basics';
  if (secondarySlugs.indexOf(slug)>=0) return 'Also your focus';
  if (alsoConsideredSlugs.indexOf(slug)>=0) return 'You also picked this';
  if (nudgeSlugs.indexOf(slug)>=0) return 'Suggested for you';
  return 'Pairs with your pack';
}

/* ---------------- cart + plan state ---------------- */
var cart = {}; cart[heroSlug] = true;        /* hero pre-selected; add-ons off */
var plan = 'mo';                             /* Monthly by default: 3-month showed $168.75 upfront as the first price (2026-09-23, Jack) */
var PLAN_KEY = 'capsi_quiz_plan';            /* the shared order's locked cadence */
var orderPlanLocked = false;                 /* true once an earlier profile set the order's plan */
var ACTIVE_PLAN_KEY = 'capsi_quiz_active_plan';  /* remember the chosen cadence across refresh (overridden by a locked order) */
try{ var _ap=localStorage.getItem(ACTIVE_PLAN_KEY); if(_ap==='mo'||_ap==='3mo'||_ap==='once') plan=_ap; }catch(e){}

/* ---- live cart sync state ----
   The selection is written to the REAL Shopify cart as it changes (not deferred to
   checkout), so the recommendation is genuinely "in your order" everywhere — the
   collection page, the cart drawer and checkout all agree. Earlier profiles' lines
   are snapshotted at load and never touched; we only add/remove this session's. */
var baselineQtyByKey = {};                   /* earlier-profile cart lines at load: line key → qty */
var baselineSnapped = false;                 /* true once /cart.js has been read at load */
var ourLineBySlug = {};                      /* slug → { key, plan } this session added to the cart */
var syncChain = Promise.resolve();           /* serialises cart writes so rapid clicks don't race */
var SESSION_LINES_KEY = 'capsi_quiz_session_lines';  /* persist ourLineBySlug across refreshes so we ADOPT our existing cart lines instead of re-adding them */
function restoreSessionLines(){ try{ var o=JSON.parse(localStorage.getItem(SESSION_LINES_KEY)||'{}'); ourLineBySlug=(o&&typeof o==='object')?o:{}; }catch(e){ ourLineBySlug={}; } }
function saveSessionLines(){ try{ localStorage.setItem(SESSION_LINES_KEY, JSON.stringify(ourLineBySlug)); }catch(e){} }

/* ---------------- helpers ---------------- */
function money(n){ if (window.theme && window.theme.formatMoney) return window.theme.formatMoney(Math.round(n*100)); return '$'+n.toFixed(2); }
function perDay(n){ return money(n)+' a day'; }
function cap(s){ return s ? s.charAt(0).toUpperCase()+s.slice(1) : s; }
function andList(a){ return a.length<=1?(a[0]||'') : a.slice(0,-1).join(', ')+' and '+a[a.length-1]; }
/* typographic apostrophe inside words ("Women's" -> "Women’s"), as in the rest of the copy */
function esc(s){ return String(s==null?'':s).replace(/(\w)'(\w)/g,'$1\u2019$2').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;'); }

/* melatonin sub-label for the Sleep pack, from the quiz decision */
function sleepSub(){ return (!Q.sleepMelatonin) ? 'no melatonin' : ''; }
function subFor(slug){ return slug==='sleep' ? sleepSub() : ''; }

/* ---------------- generated pack art (from v2) ---------------- */
function motif(kind){
  var A=INK,O=CORAL,BG='#ffffff',g='';
  if(kind==='geo'){
    g='<line x1="40" y1="186" x2="260" y2="186" stroke="'+A+'" stroke-width="0.7" opacity="0.7"/>'
     +'<path d="M90,186 a60,60 0 0 1 120,0 Z" fill="'+A+'"/>'
     +'<rect x="168" y="92" width="56" height="56" fill="'+O+'" opacity="0.95"/>'
     +'<circle cx="150" cy="186" r="60" fill="none" stroke="'+A+'" stroke-width="0.7"/>'
     +'<circle cx="150" cy="186" r="3.4" fill="'+BG+'"/><circle cx="150" cy="186" r="2" fill="'+O+'"/>';
  } else if(kind==='bars'){
    var hs=[34,58,82,50,100,66,40]; g='<line x1="58" y1="210" x2="242" y2="210" stroke="'+A+'" stroke-width="0.7"/>';
    for(var i=0;i<hs.length;i++){g+='<rect x="'+(72+i*24)+'" y="'+(210-hs[i])+'" width="14" height="'+hs[i]+'" fill="'+(i===4?O:A)+'" opacity="'+(i===4?0.95:0.82)+'"/>';}
  } else if(kind==='concentric'){
    g='<g stroke="'+A+'" stroke-width="0.7" fill="none">';
    for(var j=0;j<7;j++){g+='<path d="M'+(42+j*6)+','+(150-j*4)+' Q150,'+(66+j*6)+' '+(258-j*4)+','+(150-j*4)+' Q150,'+(234-j*4)+' '+(42+j*6)+','+(150-j*4)+' Z" opacity="'+(0.8-j*0.08)+'"/>';}
    g+='</g><circle cx="180" cy="138" r="3.5" fill="'+O+'"/>';
  } else if(kind==='cross'){
    var xs=[78,150,222],ys=[78,150,222]; g='<g stroke="'+A+'" stroke-width="0.5" opacity="0.2">';
    xs.forEach(function(x){g+='<line x1="'+x+'" y1="50" x2="'+x+'" y2="250"/>';});
    ys.forEach(function(y){g+='<line x1="50" y1="'+y+'" x2="250" y2="'+y+'"/>';}); g+='</g>';
    xs.forEach(function(x){ys.forEach(function(y){var c=x===150&&y===150,s=c?9:6,col=c?O:A;
      g+='<g stroke="'+col+'" stroke-width="'+(c?1.6:1)+'" opacity="'+(c?1:0.8)+'"><line x1="'+(x-s)+'" y1="'+y+'" x2="'+(x+s)+'" y2="'+y+'"/><line x1="'+x+'" y1="'+(y-s)+'" x2="'+x+'" y2="'+(y+s)+'"/></g>';});});
  } else if(kind==='nodes'){
    for(var r=0;r<4;r++)for(var c2=0;c2<6;c2++){var idx=r*6+c2,co=idx===8;g+='<circle cx="'+(54+c2*38)+'" cy="'+(74+r*44)+'" r="'+(co?4:2.4)+'" fill="'+(co?O:A)+'" opacity="'+(co?1:0.55)+'"/>';}
  } else {
    for(var k=0;k<5;k++){var y2=74+k*34,co2=k===1;
      g+='<rect x="52" y="'+(y2-3.5)+'" width="7" height="7" fill="'+(co2?O:A)+'" opacity="'+(co2?1:0.85)+'"/>';
      g+='<line x1="70" y1="'+y2+'" x2="248" y2="'+y2+'" stroke="'+A+'" stroke-width="0.7" opacity="'+(co2?0.9:0.45)+'"/>';
      if(co2)g+='<line x1="186" y1="'+(y2+9)+'" x2="248" y2="'+(y2+9)+'" stroke="'+O+'" stroke-width="1.6"/>';}
  }
  return '<svg viewBox="0 0 300 280" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">'+g+'</svg>';
}
/* Product image order in Shopify is [1] what's in the pack, [2] the packaging,
   [3] the lifestyle shot. The hero cycles through all of them; add-on thumbnails
   use the first image (else the featured image). */
function imagesFor(slug){
  var p=PRODUCTS[slug]; var a=(p&&p.images)||[];
  if(a.length) return a;
  var one=p&&(p.image_lg||p.image); return one?[one]:[];
}
function firstImg(slug){
  var p=PRODUCTS[slug]; var a=(p&&p.images)||[];
  return a[0]||(p&&(p.image_lg||p.image))||'';
}
function thumbArt(slug){
  var img=firstImg(slug);
  if(img) return '<img src="'+esc(img)+'" alt="" loading="lazy">';
  return motif(pack(slug).motif);
}
/* PDP link for an add-on. Empty when the pack isn't wired to a real product yet
   (dev/placeholder), so the row stays non-clickable rather than 404. */
function productUrl(slug){
  var p=PRODUCTS[slug];
  return (p&&p.handle) ? '/products/'+p.handle : '';
}

/* ---------------- shipping ----------------
   Shipping shown here has to be the number Shopify will charge at checkout, so
   nothing about the rate is hard-coded in this file:

   1. window.theme.shipping carries the store's rate settings (flat rate, free-over
      threshold, whether subscription orders ship free) from layout/theme.liquid.
      That is the ESTIMATE — used before the real cart exists, and if the live
      lookup below is unavailable.
   2. Once our selection is in the real cart, we ask Shopify itself
      (/cart/shipping_rates.json) what that cart ships for and show that instead.
      It is the same rate engine checkout runs, so it stays correct whatever the
      shipping profiles are set to — including the zero-cost rate the store offers
      on subscription orders, and any later change to the free-shipping threshold.

   The probe address is only needed because the Ajax rate endpoint refuses to
   quote without one; the store ships US-only on a single flat zone, so any US
   address returns the same rate. When several rates come back we show the
   cheapest, which is the one checkout puts first and the one anybody would pick. */
var SHIPCFG = (window.theme && window.theme.shipping) || {};
var SHIP_FLAT     = typeof SHIPCFG.flatRate === 'number' ? SHIPCFG.flatRate : 7.5;
var SHIP_FREE_OVER= typeof SHIPCFG.freeOver === 'number' ? SHIPCFG.freeOver : 100;
var SHIP_SUBS_FREE= SHIPCFG.subscriptionsFree !== false;
var SHIP_PROBE    = SHIPCFG.probe || {country:'United States', province:'California', zip:'90210'};
var liveShip = null;          /* {plan:'mo', price:0} — Shopify's own answer for the cart as it now stands */
var shipProbeSeq = 0;         /* guards against a slow probe overwriting a newer one */

/* estimate: what the configured rates say an order of this subtotal costs to ship */
function shipEstimate(planKey, subtotal){
  if(subtotal<=0) return 0;
  if(planKey!=='once' && SHIP_SUBS_FREE) return 0;
  return subtotal>=SHIP_FREE_OVER ? 0 : SHIP_FLAT;
}
/* the figure to show: Shopify's live answer when we have one for this plan, else the estimate */
function shipFor(planKey, subtotal){
  if(liveShip && liveShip.plan===planKey) return liveShip.price;
  return shipEstimate(planKey, subtotal);
}
/* Ask Shopify what the cart as it currently stands ships for. Runs after every
   cart sync; failures are silent and simply leave the estimate showing. */
function refreshShippingRate(){
  var planAtStart = plan, seq = ++shipProbeSeq;
  var a = SHIP_PROBE;
  var qs = 'shipping_address[country]='+encodeURIComponent(a.country)
         +'&shipping_address[province]='+encodeURIComponent(a.province)
         +'&shipping_address[zip]='+encodeURIComponent(a.zip);
  return fetch('/cart/shipping_rates.json?'+qs,{headers:{'X-Requested-With':'XMLHttpRequest'}})
    .then(function(r){ return r.ok?r.json():null; })
    .then(function(j){
      if(seq!==shipProbeSeq || plan!==planAtStart) return;    /* a newer probe or plan change won */
      var rates=(j&&j.shipping_rates)||[];
      if(!rates.length) return;                               /* empty cart / unquotable — keep the estimate */
      var min=null;
      rates.forEach(function(x){ var v=parseFloat(x.price); if(isFinite(v) && (min===null||v<min)) min=v; });
      if(min===null) return;
      var changed = !liveShip || liveShip.plan!==planAtStart || liveShip.price!==min;
      liveShip = {plan:planAtStart, price:min};
      if(changed) renderSummary();
    })
    .catch(function(){});
}

/* ---------------- pricing math (client-side, from base prices) ---------------- */
function monthlyBase(){ var t=0; for (var k in cart){ if (cart[k]) t+=basePrice(k); } return t; }
function calc(){
  var base=monthlyBase();
  /* Shipping is charged once for the whole order, so it is rated on everything in
     the cart — this profile's packs plus any packs earlier profiles already added. */
  var tSupply=base*3, tSave=tSupply*0.25, tTotal=tSupply-tSave;
  var mDisc=base*0.10, mAfter=base-mDisc;
  var tShip=shipFor('3mo', tTotal+committedTotal);
  var mShip=shipFor('mo',  mAfter+committedTotal);
  var oShip=shipFor('once', base+committedTotal);
  return {base:base,
    three:{supply:tSupply,save:tSave,ship:tShip,sub:tTotal,total:tTotal+tShip},
    month:{base:base,save:mDisc,after:mAfter,ship:mShip,total:mAfter+mShip},
    once:{base:base,ship:oShip,total:base+oShip}};
}
/* "spend $X more" nudge, shown only when spending more actually earns free shipping */
function freeShipNudge(subtotal){
  if(!isFinite(SHIP_FREE_OVER) || SHIP_FREE_OVER<=0) return '';
  var gap=SHIP_FREE_OVER-subtotal;
  if(gap<=0) return '';
  return '<div class="upsell">Add '+money(gap)+' more for free shipping'
    +(SHIP_SUBS_FREE?', or switch to a subscription — subscriptions ship free.':'.')+'</div>';
}

/* ---------------- render: hero ----------------
   The match card opens on the real box photo (every pack's product image 2, the
   edited phone photo of the dispenser), then the pack's other product images;
   then the pack name and "Why this pack": up to three reasons, each built from
   something the buyer actually told us. Price lives in the summary right under
   this card; the what's-inside chart + ledger also get their own section below.
   2026-09-26 rebuild (price/offer/trust): the gallery used to open on the chart. */
var heroSlide = 0;
function heroImages(slug){
  var a=imagesFor(slug).slice();
  if(a.length<2) return a;
  var bi=-1;
  a.forEach(function(src,i){ if(bi<0 && /capsi-box/i.test(src)) bi=i; });
  if(bi<0) bi=1;                       /* store order is [what's inside, box, lifestyle] */
  var box=a.splice(bi,1)[0];
  return [box].concat(a);
}
function renderHero(){
  var p=pack(heroSlug), sub=subFor(heroSlug);
  /* the real box photo only: the what's-inside chart has its own section below,
     and the capsule-map graphic reads as generic on a phone */
  var imgs=heroImages(heroSlug).slice(0,1);
  var face = imgs.length
    ? '<div class="face has-photo v2"><div class="gallery" data-gallery>'
        +imgs.map(function(src,i){ return '<img class="gimg'+(i===0?' on':'')+(i===0?' box':'')+'" src="'+esc(src)+'" alt="'+(i===0?'The Capsi dispenser box':esc(p.name))+'" width="800" height="800"'+(i===0?' loading="eager" fetchpriority="high"':' loading="lazy"')+'>'; }).join('')
      +'</div>'
      +(imgs.length>1?'<div class="gdots">'+imgs.map(function(_,i){ return '<button type="button" class="gdot'+(i===0?' on':'')+'" data-godot="'+i+'" aria-label="View image '+(i+1)+'"></button>'; }).join('')+'</div>':'')
      +'</div>'
    : '<div class="face"><div class="art">'+motif(p.motif)+'</div></div>';
  var html=
    '<div class="hero-top v2">'
    +face
    +'<div class="detail">'
      +'<h2>'+esc(p.name)+(sub?' <span class="sub">'+esc(sub)+'</span>':'')+'</h2>'
      +'<div class="pk-meta">'+p.ledger.length+' ingredients · 30 daily packets</div>'
      +'<div class="pk-offer" data-hero-offer></div>'
    +'</div>'
    +'</div>';
  var _heroEl=root.querySelector('[data-hero]');
  if(_heroEl._h!==html){ _heroEl._h=html; _heroEl.innerHTML=html; setupHeroGallery(imgs.length); }
  var _whyEl=root.querySelector('[data-why-slot]'); if(_whyEl){ var wh=whyHtml(); if(_whyEl._h!==wh){ _whyEl._h=wh; _whyEl.innerHTML=wh; } }
  renderInside();
  renderQuality();
}
/* dots + swipe for the hero photos (no auto-rotate) */
function setupHeroGallery(n){
  var face=root.querySelector('[data-hero] .face'); if(!face) return;
  var gal=face.querySelector('[data-gallery]'); if(!gal) return;
  var imgsEl=gal.querySelectorAll('.gimg'), dotsEl=face.querySelectorAll('.gdot');
  if(heroSlide>=n) heroSlide=0;
  function show(idx){
    heroSlide=idx;
    imgsEl.forEach(function(im,i){ im.classList.toggle('on', i===idx); });
    dotsEl.forEach(function(d,i){ d.classList.toggle('on', i===idx); });
  }
  dotsEl.forEach(function(d,i){ d.addEventListener('click', function(e){ e.preventDefault(); show(i); }); });
  if(n>1){
    var tsx=0, tsy=0;
    gal.addEventListener('touchstart', function(e){ var q=e.changedTouches&&e.changedTouches[0]; if(!q) return; tsx=q.clientX; tsy=q.clientY; }, {passive:true});
    gal.addEventListener('touchend', function(e){
      var q=e.changedTouches&&e.changedTouches[0]; if(!q) return;
      var dx=q.clientX-tsx, dy=q.clientY-tsy;
      if(Math.abs(dx)<40 || Math.abs(dx)<=Math.abs(dy)) return;
      show(dx<0 ? (heroSlide+1)%n : (heroSlide-1+n)%n);
    }, {passive:true});
  }
  show(heroSlide);
}

/* ---------------- "Why this pack": reasons from the buyer's own answers ----------------
   Each row pairs one answer with what we did about it. Templated per ANSWER, not
   per pack, so it works for all 29 packs. Structure/function wording only, and
   nothing is claimed that the answers + the real ledger don't support. */
var AGE_PHRASE = {"18-29":"You're under 30","30s":"You're in your 30s","40s":"You're in your 40s","50s":"You're in your 50s","60+":"You're 60 or over"};
/* what each "already taking" answer is, inside a pack ledger */
var SUPP_INGREDIENTS = { "Vit D":["D Complex"], "Magnesium":["Magnesium"], "Omega-3":["Omega 3","Vegan Omega"], "Probiotic":["10B Probiotic"], "Multivitamin":["Multivitamin"] };
var SUPP_WORD = { "Vit D":"vitamin D", "Magnesium":"magnesium", "Omega-3":"omega-3", "Probiotic":"a probiotic", "Multivitamin":"a multivitamin" };
var EAT_WORD = { greens:"leafy greens", fish:"fish", dairy:"dairy", redmeat:"red meat" };
var EAT_NUTRIENT = { fish:["Omega 3","Vegan Omega"], greens:["Magnesium"], dairy:["Calcium Citrate","D Complex"], redmeat:["Iron","Methyl B-Complex"] };
/* sentence-case an ingredient name without breaking acronyms ("DIM", "B-Complex", "10B") */
var ING_DISPLAY = { "D Complex":"vitamin D3 (with A, E and K2)", "Omega 3":"omega-3", "Vegan Omega":"algal omega-3", "10B Probiotic":"a 10-billion CFU probiotic",
  "Multivitamin":"a multivitamin", "Methyl B-Complex":"methylated B vitamins", "Herbal Sleep Blend":"a five-herb sleep blend", "Vitamin C":"vitamin C",
  "Soothing Fiber Formula":"a soothing fiber blend", "Trace Minerals":"trace minerals", "Mushroom Immune":"a 14-mushroom blend" };
function lc(name){
  if (ING_DISPLAY[name]) return ING_DISPLAY[name];
  return String(name).split(' ').map(function(w){ return (/^[A-Z]$|[A-Z].*[A-Z]|\d/.test(w)) ? w : w.toLowerCase(); }).join(' ');
}
/* "500mg" -> "500 mg", matching the what's-inside image */
function dose(d){ return String(d).replace(/(\d)\s*(mg|mcg|g|IU|CFU)\b/g,'$1\u00a0$2'); }
function ledgerRow(slug, ing){ var r=pack(slug).ledger.filter(function(x){return x[0]===ing;})[0]; return r||null; }
function ingPhrase(slug, ing){
  var r=ledgerRow(slug, ing), f=FORMS[ing];
  var bits=[]; if(f) bits.push(f); if(r && /\d/.test(r[1]) && !ING_DISPLAY[ing]) bits.push(dose(r[1]));
  return lc(ing)+(bits.length?' ('+bits.join(', ')+')':'');
}
/* the answer (besides the pick itself) that points at this pack */
var SLEEP_PHRASE = { lt5:'said you sleep under 5 hours', '56':'said you sleep 5–6 hours' };
var SIGNAL_PHRASE = {
  stress:function(){ return Q.stress==='high' ? 'said your stress is high' : (Q.stress==='elevated' ? 'said your stress is definitely elevated' : ''); },
  sleep:function(){ return SLEEP_PHRASE[Q.sleep]||''; }
};
function whyRows(){
  var rows=[], p=pack(heroSlug), led=p.ledger.map(function(r){return r[0];});
  var inside=andList(led.map(function(n){ return lc(n); }));
  var agePh=AGE_PHRASE[Q.age]||'';

  /* (i) the match itself */
  var picked=pickedConcernLabel();
  if(picked){
    var lifePack = /Perimenopause|Menopause|Hormone|PMS|Prenatal/.test(pack(heroSlug).name);
    var sig = SIGNAL_PHRASE[heroSlug] ? SIGNAL_PHRASE[heroSlug]() : '';
    var lead = (lifePack && agePh) ? agePh+' and picked ' : 'You picked ';
    rows.push({t:lead+'“'+lcFirst(picked)+'”'+(sig?' and '+sig:'')+'.', b:'This pack has '+inside+'.'});
  } else if(heroSlug===baseSlug){
    rows.push({t:'You asked for a daily foundation.', b:'This pack has '+inside+'.'});
  } else {
    rows.push({t:'It’s the closest match to what you picked.', b:'It has '+inside+'.'});
  }

  /* (ii) their current supplements: what this pack replaces, what it doesn't */
  var supps=(Q.currentSupps||[]).filter(function(s){ return SUPP_WORD[s]; });
  if(supps.length){
    var have=supps.filter(function(s){ return (SUPP_INGREDIENTS[s]||[]).some(function(i){ return led.indexOf(i)>=0; }); });
    var rest=supps.filter(function(s){ return have.indexOf(s)<0; });
    var restW=andList(rest.map(function(s){return SUPP_WORD[s].replace(/^a /,'');}));
    var b;
    if(have.length && rest.length){
      var hp=have.map(function(s){ var ing=(SUPP_INGREDIENTS[s]||[]).filter(function(i){return led.indexOf(i)>=0;})[0]; return ingPhrase(heroSlug, ing); });
      b='Its '+andList(hp)+' replaces yours. Keep your '+restW+'.'+baseCovers(rest);
    } else if(have.length){
      b='This pack covers '+(have.length>1?'all of those':'it')+', so you can stop buying '+(have.length>1?'them':'it')+' separately.';
    } else {
      b='This pack doesn’t include '+(rest.length>1?'any of those':'it')+', so keep taking '+(rest.length>1?'them':'it')+'.'+baseCovers(rest);
    }
    rows.push({t:'You already take '+andList(supps.map(function(s){return SUPP_WORD[s];}))+'.', b:b});
  } else if(Q.q1==='none'){
    rows.push({t:'You don’t take anything today.', b:'So we’re starting you with one pack: one packet with breakfast, nothing else to buy.'});
  } else if(Q.q1==='onoff'){
    rows.push({t:'You said you tend to start and stop.', b:'Everything comes in one packet a day, so there’s nothing to sort.'});
  }

  /* (iii) their other picks, else a diet gap this pack covers, else low winter sun */
  var otherGoals=goals.filter(function(g){ return g!==primaryGoal && PACK_CATALOG[slugOf(g.pack)] && slugOf(g.pack)!==heroSlug; })
    .concat((Q._alsoConsidered||[]).map(function(a){ return {pack:a.pack}; }))
    .filter(function(g){ return (Q.goals||[]).some(function(x){ return x && x.pack===g.pack; }); });
  if(otherGoals.length===1){
    var g0=otherGoals[0], l0=CONCERN_LABELS[g0.pack] ? lcFirst(CONCERN_LABELS[g0.pack]) : pack(slugOf(g0.pack)).name;
    rows.push({t:'You also picked '+l0+'.', b:'The '+pack(slugOf(g0.pack)).name+' covers that. You’ll find it below if you want both.'});
  } else if(otherGoals.length>1){
    var lAll=otherGoals.map(function(g){ return '“'+(CONCERN_LABELS[g.pack] ? lcFirst(CONCERN_LABELS[g.pack]) : pack(slugOf(g.pack)).name)+'”'; });
    rows.push({t:'You also picked '+(lAll.length>3 ? lAll.slice(0,3).join(', ')+' and '+(lAll.length-3)+' other'+(lAll.length-3>1?'s':'') : andList(lAll))+'.', b:otherShown(otherGoals)});
  } else {
    var low=(Q.lowEat||[]).filter(function(e){ return (EAT_NUTRIENT[e]||[]).some(function(i){ return led.indexOf(i)>=0; }); });
    if(low.length){
      var nut=[]; low.forEach(function(e){ (EAT_NUTRIENT[e]||[]).forEach(function(i){ if(led.indexOf(i)>=0 && nut.indexOf(i)<0) nut.push(i); }); });
      rows.push({t:'You rarely eat '+andList(low.map(function(e){return EAT_WORD[e];}))+'.', b:'This pack has '+andList(nut.map(function(i){return ingPhrase(heroSlug,i);}))+'.'});
    } else if(Q.sun==='long' && led.indexOf('D Complex')>=0){
      rows.push({t:'Your winters are long and dark.', b:'This pack has vitamin D3.'});
    }
  }
  return rows.slice(0,3);
}
/* the daily base pack covers some of what they'd still be buying separately */
/* name only the packs actually listed in "Also matched" (first-timers see two) */
function otherShown(og){
  var names=og.map(function(g){ return slugOf(g.pack); }).filter(function(s){ return addonSlugs.indexOf(s)>=0; }).map(function(s){ return pack(s).name; });
  if(!names.length) return 'One pack is plenty to start.';
  if(names.length>2) return 'One pack is plenty to start. Their packs are below if you want to add one later.';
  return 'One pack is plenty to start. The '+andList(names)+(names.length>1?' are':' is')+' below if you want to add '+(names.length>1?'one':'it')+' later.';
}
function baseCovers(rest){
  if(baseSlug===heroSlug || !PACK_CATALOG[baseSlug]) return '';
  var bl=pack(baseSlug).ledger.map(function(r){return r[0];});
  var cov=rest.filter(function(s){ return (SUPP_INGREDIENTS[s]||[]).some(function(i){ return bl.indexOf(i)>=0; }); });
  if(!cov.length) return '';
  return ' Or add the '+pack(baseSlug).name+' below: it covers your '+andList(cov.map(function(s){return SUPP_WORD[s].replace(/^a /,'');}))+' too.';
}
function lcFirst(s){ return s ? s.charAt(0).toLowerCase()+s.slice(1) : s; }
function whyHtml(){
  var rows=whyRows(), ix=['i','ii','iii'];
  var melNote = (heroSlug==='sleep' && Q.melatoninScreened && !Q.sleepMelatonin && Q.melatoninReason)
    ? '<div class="wrow"><span class="wix">–</span><div><b>No melatonin.</b> We left it out because '+esc(Q.melatoninReason)+'.</div></div>' : '';
  if(!rows.length && !melNote) return '';
  return '<div class="why" data-why>'
    +'<div class="why-h">Why we picked it'+(firstName?' for you, '+esc(firstName):'')+'</div>'
    +rows.map(function(r,i){ return '<div class="wrow"><span class="wix">'+ix[i]+'</span><div><b>'+esc(r.t)+'</b> '+esc(r.b)+'</div></div>'; }).join('')
    +melNote
  +'</div>';
}

/* ---------------- render: what's inside ----------------
   The pack's own what's-inside chart (product image 1, real OK Capsule pills)
   plus the ledger with each ingredient's form where we've verified it. */
function renderInside(){
  var el=root.querySelector('[data-inside]'); if(!el) return;
  var img=firstImg(heroSlug), p=pack(heroSlug);
  var h=(img?'<img class="inside-img" src="'+esc(img)+'" alt="What’s inside the '+esc(p.name)+'" width="800" height="800" loading="lazy">':'')
    +ledgerHtml(heroSlug)+warnHtml(heroSlug);
  if(el._h!==h){ el._h=h; el.innerHTML=h; }
}
function warnHtml(slug){
  var seen={}, lines=[];
  pack(slug).ledger.forEach(function(r){ var w=ING_WARN[r[0]]; if(w && !seen[w]){ seen[w]=1; lines.push(w); } });
  var older = Q.age==='60+' || Q.gender==='male';
  lines.push('Taking any prescription medicine (for example a blood thinner, or thyroid, blood pressure, diabetes, sleep or anxiety medicine)'
    +(older?'':', pregnant or nursing,')+' or managing a medical condition? Show this ingredient list to your doctor or pharmacist before you start.');
  return '<div class="warn"><b>Before you start</b>'+lines.map(function(l){return '<p>'+esc(l)+'</p>';}).join('')+'</div>';
}
function ledgerHtml(slug){
  var l=pack(slug).ledger; if(!l.length) return '';
  return '<div class="ledger"><div class="ledger-note">One capsule per ingredient, all in one packet.</div>'+l.map(function(row){
    var f=ING_FACTS[row[0]] && ING_FACTS[row[0]].form;
    return '<div class="lrow"><span class="lr-n">'+esc(row[0])+(f?'<span class="lr-f">'+esc(f)+'</span>':'')+'</span><span class="lr-d">'+esc(dose(row[1]))+'</span></div>';
  }).join('')+'</div>';
}

/* "Sarah is already in your order" banner — shown when earlier profiles have been
   committed to the shared cart. Sits under the page intro. */
/* The name is optional in the assessment, so an earlier profile may have none:
   label it by position ("Profile 1") so every sentence below still reads. */
function profLabel(p,i){ return (p&&p.name) ? p.name : 'Profile '+(i+1); }
function renderCommitted(){
  var ex=root.querySelector('[data-committed]'); if(ex) ex.remove();
  var list=getProfiles();
  if(!list.length || !committedCount) return;
  var names=list.map(function(p,i){return esc(profLabel(p,i));});
  var packs=list.reduce(function(n,p){return n+(p.count||0);},0);
  var el=document.createElement('div');
  el.className='committed'; el.setAttribute('data-committed','');
  el.innerHTML='<span class="ci">✦</span><span><b>'+andList(names)+' '+(list.length>1?'are':'is')
    +' already in your order</b> ('+packs+' pack'+(packs===1?'':'s')+'). You’re now building '
    +esc(firstName||'the next profile')+'’s set. Everyone ships together in one delivery.</span>';
  var intro=root.querySelector('.intro'); if(intro) intro.appendChild(el);
}

/* ---------------- render: timeline (pack-agnostic) ----------------
   A routine timeline, not an outcome promise: the old "Weeks 4–8 … most people
   start to notice a difference" was an outcome-timeline claim (claims bank), and
   its "that's why we recommend the 3-month plan" contradicted the Monthly default.
   The 8–12 week figure describes the research, not what the buyer will feel. */
var EXPECT_STEPS=[
  ["Day 1","Take one packet with breakfast."],
  ["Weeks 1–4","Take it daily. If something doesn’t agree with you, email us."],
  ["Months 2–3","Most of these ingredients were studied over 8–12 weeks of daily use. Give it that long before you decide."]
];
function renderTimeline(){
  var h='';
  EXPECT_STEPS.forEach(function(s){ h+='<div class="tstep"><span class="tnode"></span><div class="tw">'+esc(s[0])+'</div><p>'+esc(s[1])+'</p></div>'; });
  root.querySelector('[data-tsteps]').innerHTML=h;
}

/* ---------------- render: add-ons ("Also matched for you", folded) ----------------
   One closed row by default so the first order stays one pack; compact rows with
   the price on the SAME plan as the hero (it used to show the one-time price
   labelled "/mo"). */
function planPrice(slug){ var b=basePrice(slug); return plan==='3mo' ? b*0.75 : (plan==='mo' ? b*0.90 : b); }
function renderRoutine(){
  var h='';
  addonSlugs.forEach(function(slug){
    var p=pack(slug), on=cart[slug], sub=subFor(slug);
    var cl = CONCERN_LABELS[p.name.replace(/ Pack$/,'')];
    var note = (alsoReasonBySlug[slug] && cl) ? 'You picked “'+lcFirst(cl)+'”.' : (alsoReasonBySlug[slug] || p.why);
    if (slug===baseSlug && (Q.currentSupps||[]).indexOf('Multivitamin')>=0 && p.ledger.some(function(r){return r[0]==='Multivitamin';}))
      note = 'Has a multivitamin, so it replaces yours.';
    else if (secondarySlugs.indexOf(slug)>=0 && CONCERN_LABELS[p.name.replace(/ Pack$/,'')])
      note = 'You picked “'+lcFirst(CONCERN_LABELS[p.name.replace(/ Pack$/,'')])+'”.';
    var url=productUrl(slug);
    var thumb = url
      ? '<a class="thumb" href="'+esc(url)+'" aria-label="View '+esc(p.name)+'">'+thumbArt(slug)+'</a>'
      : '<div class="thumb">'+thumbArt(slug)+'</div>';
    var name = esc(p.name)+(sub?' <span class="sub">'+esc(sub)+'</span>':'');
    var nameHtml = url ? '<a class="nm" href="'+esc(url)+'">'+name+'</a>' : '<div class="nm">'+name+'</div>';
    h+='<div class="ritem">'
      +thumb
      +'<div class="meta"><div class="role">'+esc(roleLabel(slug))+'</div>'+nameHtml+'<p>'+esc(note)+'</p></div>'
      +'<div class="right"><div class="pr">'+money(planPrice(slug))+'<span class="per">'+(plan==='once'?' one-time':'/mo')+'</span></div>'
      +'<button class="addbtn'+(on?' on':'')+'" data-toggle="'+slug+'" aria-pressed="'+(on?'true':'false')+'">'+(on?'Added ✓':'+ Add')+'</button></div>'
    +'</div>';
  });
  var _rtEl=root.querySelector('[data-routine]'); if(_rtEl._h!==h){ _rtEl._h=h; _rtEl.innerHTML=h; }
  var n=addonSlugs.length, added=addonSlugs.filter(function(s){return cart[s];}).length;
  var cnt=root.querySelector('[data-routine-count]');
  if(cnt) cnt.textContent = added ? added+' added' : n+' pack'+(n===1?'':'s')+', optional';
  var sec=root.querySelector('[data-routine-sec]');
  if(sec) sec.hidden = !n;
}

/* ---------------- render: summary ----------------
   2026-09-27 (B): subscribe-and-save plan cards, the pattern Ritual, AG1 and
   Perelel use. Monthly (default), every 3 months (best value) and one-time
   are all visible at once, each with its price, saving and what it means,
   then one CTA carrying the price, a trust row, and the receipt-style
   breakdown in a fold. (An earlier pass hid 3-month/one-time in a text line.) */
var brkOpen = false;
/* what the plan charges per billing (packs only, before shipping) and the first
   charge after the first-order discount */
function planCharge(c, k){ return k==='3mo' ? c.three.sub : (k==='mo' ? c.month.after : c.once.base); }
function firstCharge(c, k){ var v=planCharge(c,k); return foFor(k) ? v*(1-FO_PCT/100) : v; }
/* what they pay today: first charge plus shipping (one-time pays $7.50 under $100) */
function todayCharge(c, k){ return firstCharge(c,k)+(k==='3mo'?c.three.ship:(k==='mo'?c.month.ship:c.once.ship)); }
function planCardsHtml(c, count){
  var m=c.month, three=c.three, once=c.once, forN = count>1 ? ' for '+count+' packs' : '';
  var cards=[
    {k:'mo', name:'Monthly', badge:'', price:money(m.after), per:'/mo', was:'', save:'Save 10%',
     day:money(m.total/30)+' a day', lines:['Monthly delivery','Cancel or pause anytime']},
    {k:'3mo', name:'Every 3 months', badge:'Best value', price:money(three.sub/3), per:'/mo', was:'', save:'Save 25%',
     day:money(three.total/90)+' a day', lines:['Billed '+money(three.sub)+' every 3 months','Packs still arrive monthly']},
    {k:'once', name:'One-time', badge:'', price:money(once.base), per:'', was:'', save:'', always:1,
     day:money(once.total/30)+' a day', lines:['One month, no subscription', once.ship===0?'Free shipping':money(once.ship)+' shipping']}
  ];
  if(orderPlanLocked) cards=cards.filter(function(x){ return x.k===plan; });
  return '<div class="plans-h">'+(orderPlanLocked?'Your order’s plan':'Choose how often'+forN)+'</div>'
    +'<div class="plans" role="radiogroup" aria-label="Purchase plan">'
    +cards.map(function(x){
      var on = x.k===plan;
      return '<button type="button" class="plan'+(on?' sel':'')+(x.always?' all':'')+'" role="radio" aria-checked="'+(on?'true':'false')+'"'
        +(orderPlanLocked?' disabled':' data-plan="'+x.k+'" data-plan-via="card"')+'>'
        +'<span class="pl-radio" aria-hidden="true"></span>'
        +'<span class="pl-main">'
          +'<span class="pl-top"><span class="pl-name">'+x.name+'</span>'+(x.badge?'<span class="pl-badge">'+x.badge+'</span>':'')+'</span>'
          +'<span class="pl-lines">'+x.lines.map(function(l){return '<span>'+esc(l)+'</span>';}).join('')+'</span>'
        +'</span>'
        +'<span class="pl-price"><span class="pl-now">'+x.price+'<small>'+x.per+'</small></span>'
          +(x.save?'<span class="pl-was">'+x.save+'</span>':'')
          +(on?'<span class="pl-day">'+x.day+'</span>':'')
        +'</span>'
        +(on && foFor(x.k) && !orderPlanLocked ? '<span class="pl-first">'+(x.k==='3mo'?'First 3 months ':'First month ')+'<b>'+money(firstCharge(c,x.k))+'</b> with '+FO_PCT+'% off your first pack</span>' : '')
      +'</button>';
    }).join('')
    +'</div>';
}
function foLines(c,k){
  if(!foFor(k)) return '';
  var cut=planCharge(c,k)*FO_PCT/100, ship=(k==='3mo'?c.three.ship:(k==='mo'?c.month.ship:c.once.ship));
  return '<div class="ln save"><span class="l">First-order discount ('+FO_PCT+'%)</span><span class="v">−'+money(cut)+'</span></div>'
    +'<div class="ln total fo"><span class="l">Your first order</span><span class="v">'+money(planCharge(c,k)-cut+ship)+'</span></div>';
}
function breakdownHtml(c){
  var three=c.three, m=c.month;
  if(plan==='3mo') return '<div class="ln"><span class="l">3 months’ supply, delivered monthly</span><span class="v">'+money(three.supply)+'</span></div>'
      +'<div class="ln save"><span class="l">Subscription saving (25%)</span><span class="v">−'+money(three.save)+'</span></div>'
      +'<div class="ln"><span class="l">Shipping</span><span class="v">'+(three.ship===0?'Free':money(three.ship))+'</span></div>'
      +(three.ship>0?freeShipNudge(three.sub+committedTotal):'')
      +'<div class="ln total"><span class="l">Total</span><span class="v">'+money(three.total)+'</span></div>'
      +foLines(c,'3mo')
      +'<div class="beforetax">One payment every 3 months, before tax</div>';
  if(plan==='mo') return '<div class="ln"><span class="l">1 month’s supply</span><span class="v">'+money(m.base)+'</span></div>'
      +'<div class="ln save"><span class="l">Subscription saving (10%)</span><span class="v">−'+money(m.save)+'</span></div>'
      +'<div class="ln"><span class="l">Shipping</span><span class="v">'+(m.ship===0?'Free':money(m.ship))+'</span></div>'
      +(m.ship>0?freeShipNudge(m.after+committedTotal):'')
      +'<div class="ln total"><span class="l">Total per month</span><span class="v">'+money(m.total)+'</span></div>'
      +foLines(c,'mo')
      +'<div class="beforetax">Billed monthly, before tax</div>';
  return '<div class="ln"><span class="l">1 month’s supply, one-time</span><span class="v">'+money(c.once.base)+'</span></div>'
      +'<div class="ln"><span class="l">Shipping</span><span class="v">'+(c.once.ship===0?'Free':money(c.once.ship))+'</span></div>'
      +(c.once.ship>0?freeShipNudge(c.once.base+committedTotal):'')
      +'<div class="ln total"><span class="l">Total</span><span class="v">'+money(c.once.total)+'</span></div>'
      +foLines(c,'once')
      +'<div class="beforetax">One payment, before tax</div>';
}
/* live list of what's in the cart (shown once the selection is more than the
   hero alone), each with a × to remove it */
function cartListHtml(){
  var selected=[heroSlug].concat(addonSlugs).filter(function(s){return cart[s];});
  if(!selected.length) return '';
  return '<div class="sumpacks">'+selected.map(function(s){
    return '<div class="sumpack"><span class="sp-nm">'+esc(pack(s).name)+'</span>'
      +'<button class="sp-x" data-toggle="'+s+'" aria-label="Remove '+esc(pack(s).name)+'">×</button></div>';
  }).join('')+'</div>';
}
/* packs already committed to the shared order by earlier profiles — shown read-only
   above the current person's selection so the summary reflects the whole order. */
function committedListHtml(){
  var profs=getProfiles(); if(!profs.length||!committedCount) return '';
  return '<div class="sumcommitted">'+profs.map(function(pf,i){
    var nm=esc(profLabel(pf,i)), n=pf.count||0;
    return '<div class="sumpack iscommitted"><span class="sp-nm">'+nm+'’s packs</span>'
      +'<span class="sp-r"><span class="sp-pr">'+n+(n===1?' pack':' packs')+'</span><span class="sp-tag">in order</span></span></div>';
  }).join('')+'</div>';
}
function selectionCount(){ var n=0; for (var k in cart){ if (cart[k]) n++; } return n; }
function heroOnly(){ return selectionCount()===1 && cart[heroSlug]; }
function ctaLabel(count, amount){
  if(!count) return 'Add a pack to start';
  var lead = (heroOnly() && !committedCount) ? (plan==='once' ? 'Buy the '+pack(heroSlug).name : 'Start my '+pack(heroSlug).name)
                                             : 'Checkout · '+(count+committedCount)+' pack'+((count+committedCount)===1?'':'s');
  return lead;
}
function renderSummary(){
  var c=calc(), count=selectionCount(), empty=c.base===0;
  var sel = plan==='3mo'?c.three:(plan==='mo'?c.month:c.once);
  var perDayVal = plan==='3mo'?c.three.total/90:(plan==='mo'?c.month.total/30:c.once.total/30);
  var profs=getProfiles();
  var grandHtml = (committedCount && profs.length)
    ? '<div class="grandtotal"><span class="l">Order total</span><span class="v">'+money(sel.total+committedTotal)+'</span></div>'
      +'<p class="grand-note">Includes '+andList(profs.map(function(p,i){return esc(profLabel(p,i));}))+'’s packs already in your order.</p>'
    : '';
  var inner=committedListHtml()
    +(heroOnly()?'':cartListHtml())
    +(count>=2?'<div class="ship-note">✦&nbsp;<span><b>All your packs arrive in one box.</b></span></div>':'')
    +(empty?'':planCardsHtml(c, count))
    +grandHtml
    +'<button class="checkout"'+(empty?' disabled':'')+' data-checkout>'+esc(ctaLabel(count))
      +(empty?'':'<span class="co-sub">'+money(todayCharge(c,plan))+' today'+(plan==='once'?'':', then '+money(planCharge(c,plan))+(plan==='3mo'?' every 3 months':'/mo'))+'</span>')+'</button>'
    +(empty?'':'<div class="trustrow">'
        +'<span><svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M2.5 6.5h9v7h-9zM11.5 9h3.2l2.8 2.6v1.9h-6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><circle cx="5.5" cy="14.5" r="1.4" fill="none" stroke="currentColor" stroke-width="1.2"/><circle cx="14.5" cy="14.5" r="1.4" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>Free shipping on subscriptions</span>'
        +'<span><svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M10 2.5l6 2.3v4.6c0 3.9-2.6 6.6-6 8.1-3.4-1.5-6-4.2-6-8.1V4.8z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M7.2 10l2 2 3.8-4" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>30-day refund on your first order</span>'
        +(plan==='once'?'':'<span><svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M10 6v4.2l2.6 1.6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>Cancel or pause online anytime, no call needed</span>')
      +'</div>'
      +'<details class="brk"'+(brkOpen?' open':'')+'><summary>See the breakdown</summary><div class="lines">'+breakdownHtml(c)+'</div></details>');
  /* Render only the summary visible at this breakpoint (desktop sticky aside vs
     mobile inline), and skip the write when the markup is unchanged so async
     price hydration doesn't churn the DOM or shift numbers post-paint. */
  var isMobileSum = window.matchMedia ? window.matchMedia('(max-width:920px)').matches : (window.innerWidth<=920);
  var sumEl = root.querySelector(isMobileSum ? '[data-summary-mobile]' : '[data-summary]');
  if (sumEl && sumEl._h!==inner){ sumEl._h=inner; sumEl.innerHTML=inner; }

  /* header pill reflects the whole shared order: this person + committed profiles */
  var allCount = count + committedCount;
  setText('[data-hdr-amt]', money((empty ? 0 : todayCharge(c,plan)) + committedTotal));
  setText('[data-hdr-count]', allCount+(allCount===1?' pack':' packs'));
  var nameK = heroOnly()?pack(heroSlug).name:count+(count===1?' pack':' packs');
  if(foFor(plan) && !empty){
    setText('[data-mbar-k]', 'then '+money(planCharge(c,plan))+(plan==='3mo'?' every 3 months':'/mo'));
    setText('[data-mbar-v]', money(firstCharge(c,plan)));
    setText('[data-mbar-per]', plan==='3mo'?' first 3 months':(plan==='mo'?' first month':' today'));
  } else {
    setText('[data-mbar-k]', nameK+(empty?'':' · '+money(perDayVal)+' a day'));
    setText('[data-mbar-v]', money(sel.total));
    setText('[data-mbar-per]', plan==='3mo'?' / 3 months':(plan==='mo'?' / mo':''));
  }
  renderHeroOffer(c, empty);
  var mbar=root.querySelector('[data-mbar]'); if(mbar) mbar.hidden=false;
}
/* price + offer under the pack name in the hero, so the value is above the fold */
function renderHeroOffer(c, empty){
  var el=root.querySelector('[data-hero-offer]'); if(!el) return;
  var h='';
  if(!empty){
    var rec=planCharge(c,plan), first=todayCharge(c,plan);
    var then = plan==='mo' ? 'then '+money(rec)+'/mo · cancel online anytime'
             : (plan==='3mo' ? 'then '+money(rec)+' every 3 months' : 'one-time, no subscription'+(c.once.ship?', includes '+money(c.once.ship)+' shipping':''));
    h = (foFor(plan)?'<span class="po-badge">'+FO_PCT+'% off your first subscription pack</span>':(foOn()?'<span class="po-badge soft">Subscribe for '+FO_PCT+'% off your first pack</span>':''))
      +'<div class="po-price"><span class="po-now">'+money(first)+'</span>'
        +(foFor(plan)?'<s class="po-was">'+money(rec)+'</s>':'')
        +'<span class="po-per">'+(plan==='3mo'?'first 3 months':(plan==='mo'?'first month':'today'))+'</span></div>'
      +'<div class="po-then">'+then+'</div>'
      +'<div class="po-legit"><svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true"><path d="M10 2.5l6 2.3v4.6c0 3.9-2.6 6.6-6 8.1-3.4-1.5-6-4.2-6-8.1V4.8z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M7.2 10l2 2 3.8-4" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>Made to order in a cGMP-certified facility · third-party tested</div>';
  }
  if(el._h!==h){ el._h=h; el.innerHTML=h; }
}
/* "How it's made": facts from the manufacturer's records, some specific to this pack */
function renderQuality(){
  var el=root.querySelector('[data-quality]'); if(!el) return;
  var p=pack(heroSlug);
  var free=packFreeFrom(heroSlug).map(function(a){ return ALLERGEN_WORD[a]; });
  var rows=[
    ['Made to order, released by QA', 'Your pack is made to order in a cGMP-certified facility, and every batch passes a quality-assurance release before it ships.'],
    ['Third-party tested', 'Every batch is tested for potency and purity by an ISO/IEC 17025-accredited lab. Ask us for the Certificate of Analysis.'],
    free.length>=4 ? ['Free from '+(free.length>3 ? free.slice(0,3).join(', ')+' and more' : andList(free)), 'Every ingredient in this pack is free from '+andList(free)+(packPlantBased(heroSlug)?', and it’s fully plant-based':'')+'.'] : null,
    ['The full facts, in the box', 'A printed booklet with every ingredient, form and dose comes with your first delivery.']
  ].filter(Boolean);
  var h=rows.map(function(r,i){ return '<div class="q-row"><span class="q-n">0'+(i+1)+'</span><div><b>'+esc(r[0])+'</b><p>'+esc(r[1])+'</p></div></div>'; }).join('');
  if(el._h!==h){ el._h=h; el.innerHTML=h; }
}
function setText(sel,txt){ txt=String(txt).replace(/(\w)'(\w)/g,'$1\u2019$2'); root.querySelectorAll(sel).forEach(function(el){ el.textContent=txt; }); }

function renderAll(){ renderHero(); renderRoutine(); renderSummary(); }

/* ---------------- personalisation ----------------
   Headline names the match. The old subhead read signals.primary_concern, an
   analytics bucket, so Joint Health / Bone Builder matches were told "about your
   performance"; the reasons now live in "Why this pack" instead. The packet-name
   line is left off for a 2nd+ profile: OK Capsule prints one name per order. */
function personalise(){
  var hn=pack(heroSlug).name;
  root.querySelector('[data-headline]').innerHTML =
    (firstName ? esc(firstName)+', <em>your</em>' : '<em>Your</em>')+' match is the '+esc(hn)+'<span class="dot">.</span>';
  var pc=pickedConcernLabel();
  root.querySelector('[data-subhead]').innerHTML = pc ? 'Matched to “'+esc(lcFirst(pc))+'” and your other answers.' : 'Matched from your answers.';
}

/* ---------------- results analytics ----------------
   What people do on this page used to be invisible: GA4/Meta only saw a bare
   add_to_cart when a plan tap or pack toggle re-synced the cart, with no plan
   and no hint of which it was. These go to Shopify customer events; the "GA4"
   custom pixel must subscribe to each name (snippet in
   docs/RESULTS_TRACKING.md). No health answers are sent — pack + plan only. */
/* A/B test 2026-09-29: the same results events, readable without GA4.
   - Clarity: tag the session with the variant and pack, so recordings,
     heatmaps and scroll depth filter by A/B in the Clarity dashboard.
   - Klaviyo: "Viewed Results" and "Clicked Results Checkout" on the profile
     that finished the assessment (public client endpoint, same pattern as
     quiz.js). Pack, plan and variant only, never health answers. */
var KL_EVENT = { results_view:'Viewed Results', results_checkout_click:'Clicked Results Checkout' };
function mirrorEvent(ev, props){
  try{
    if(typeof window.clarity==='function'){
      if(ev==='results_view'){ window.clarity('set','results_variant',String(props.results_variant)); window.clarity('set','match_pack',String(props.match_pack)); }
      else if(ev==='results_checkout_click'){ window.clarity('event','results_checkout_click'); }
    }
    var name=KL_EVENT[ev], kid=window.theme && window.theme.klaviyoPublicId, email=data && data.email;
    if(!name || !kid || !email) return;
    if(ev==='results_view'){   /* once per browser session per match, not per reload */
      var seenKey='capsi_kl_results_view_'+props.results_variant+'_'+props.match_pack;
      try{ if(sessionStorage.getItem(seenKey)) return; sessionStorage.setItem(seenKey,'1'); }catch(e){}
    }
    fetch('https://a.klaviyo.com/client/events/?company_id='+encodeURIComponent(kid), {
      method:'POST', keepalive:true,   /* the checkout click redirects straight away */
      headers:{ 'Content-Type':'application/json', 'revision':'2024-10-15' },
      body:JSON.stringify({ data:{ type:'event', attributes:{
        properties:{ results_variant:props.results_variant, match_pack:props.match_pack, plan:props.plan,
                     packs:props.packs, packs_selected:props.packs_selected },
        value: ev==='results_checkout_click' ? props.order_total : undefined,
        metric:{ data:{ type:'metric', attributes:{ name:name } } },
        profile:{ data:{ type:'profile', attributes:{ email:email } } }
      } } })
    }).catch(function(){});
  }catch(e){}
}
function rtrack(ev, extra){
  try{
    var c=calc();
    var total = plan==='3mo'?c.three.total:(plan==='mo'?c.month.total:c.once.total);
    var packs = Object.keys(cart).filter(function(k){ return cart[k]; });
    var props = { match_pack:heroSlug, plan:plan, packs_selected:packs.length,
                  packs:packs.join(','), order_total:Math.round(total*100)/100,
                  results_variant:root.getAttribute('data-results-variant')||'b' };  /* A/B test 2026-09-27 */
    for (var k in (extra||{})) props[k]=extra[k];
    (window.dataLayer=window.dataLayer||[]).push(Object.assign({event:ev},props));
    if(window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish==='function') Shopify.analytics.publish(ev, props);
    mirrorEvent(ev, props);
  }catch(e){}
}

/* ---------------- events ---------------- */
document.addEventListener('click', function(e){
  var t=e.target.closest('[data-toggle]');
  if(t){ var s=t.getAttribute('data-toggle'); cart[s]=!cart[s]; liveShip=null; renderAll(); syncCart(); rtrack('results_pack_toggle',{pack:s, action:cart[s]?'add':'remove', is_match:s===heroSlug}); return; }
  var pl=e.target.closest('[data-plan]');
  if(pl){ var fromPlan=plan; plan=pl.getAttribute('data-plan'); rtrack('results_plan_select',{from_plan:fromPlan, via:pl.getAttribute('data-plan-via')||'toggle'}); try{ localStorage.setItem(ACTIVE_PLAN_KEY, plan); }catch(e2){} liveShip=null; renderAll(); syncCart(); return; }
  var co=e.target.closest('[data-checkout]');
  if(co){ e.preventDefault(); rtrack('results_checkout_click'); checkout(co); return; }
  var pv=e.target.closest('a[href*="/products/"]');
  if(pv){ var m=/\/products\/([^/?#]+)/.exec(pv.getAttribute('href')||''); rtrack('results_view_product',{pack:m?m[1]:''}); }
  var ap=e.target.closest('[data-add-profile]');
  if(ap){ e.preventDefault(); ap.classList.add('saving'); ap.textContent='Saving your order…'; addSecondProfile(); return; }
  var ct=e.target.closest('[data-cart-toggle]');
  if(ct){
    e.preventDefault();
    /* The Shopify drawer is suppressed on this immersive page, so the cart button
       jumps to the on-page summary (sticky aside on desktop, inline copy on mobile)
       and flashes it — otherwise on desktop, where the summary is already in view,
       the click looks like nothing happened. (Items are now genuinely in the cart
       via syncCart, so the collection page + drawer reflect them off this page.) */
    var aside=root.querySelector('.aside');
    var dest=(aside && aside.offsetParent!==null) ? aside : root.querySelector('.inline-summary');
    if(dest){
      dest.scrollIntoView({behavior:'smooth',block:'start'});
      var card=dest.querySelector('.summary');
      if(card){ card.classList.remove('flash'); void card.offsetWidth; card.classList.add('flash'); setTimeout(function(){ card.classList.remove('flash'); },1500); }
    }
    return;
  }
  var jump=e.target.closest('[data-mbar-jump]');
  if(jump){ var is=root.querySelector('.inline-summary'); if(is) is.scrollIntoView({behavior:'smooth',block:'start'}); return; }
});

/* folds: the add-ons row and the price breakdown (toggle doesn't bubble, so capture) */
root.addEventListener('toggle', function(e){
  var d=e.target;
  if(d.matches && d.matches('[data-routine-sec]') && d.open) rtrack('results_addons_open',{addons:addonSlugs.length});
  if(d.matches && d.matches('.brk')) brkOpen=d.open;
}, true);
/* seen-once events for the two blocks built to answer "why this" and "what if" */
(function(){
  if(!('IntersectionObserver' in window)) return;
  var seen={};
  var io=new IntersectionObserver(function(entries){
    entries.forEach(function(en){
      if(!en.isIntersecting) return;
      var ev=en.target.getAttribute('data-seen-ev');
      if(ev && !seen[ev]){ seen[ev]=1; rtrack(ev); io.unobserve(en.target); }
    });
  },{threshold:0.5});
  window.__capsiWatch=function(el,ev){ if(el && !el.__w){ el.__w=1; el.setAttribute('data-seen-ev',ev); io.observe(el); } };
})();

/* ---------------- live cart sync + checkout ---------------- */
function fetchProduct(handle){
  if(productCache[handle]) return Promise.resolve(productCache[handle]);
  return fetch('/products/'+handle+'.js',{headers:{'X-Requested-With':'XMLHttpRequest'}})
    .then(function(r){ return r.ok?r.json():null; })
    .then(function(p){ productCache[handle]=p; return p; })
    .catch(function(){ return null; });
}
/* effective per-month price of an allocation. Two plan shapes exist:
   - native prepaid: per_delivery_price is already the per-month figure.
   - custom-price bundle (current Seal setup): the plan is "deliver every 3 months"
     at a fixed price of base×3×0.75, so price AND per_delivery_price are both the
     full bundle amount — divide by the plan's months to get per-month.
   We try both readings and keep whichever lands closer to the target discount. */
function allocPerMonth(a, variant, target, months){
  var unit=(a.per_delivery_price!=null?a.per_delivery_price:a.price);
  if(months>1){
    var alt=a.price/months;
    if(Math.abs((1-alt/variant.price)-target) < Math.abs((1-unit/variant.price)-target)) unit=alt;
  }
  return unit;
}
/* pick the selling plan allocation whose per-month discount best matches the target (.10/.25) */
function pickAllocation(variant, target, months){
  var allocs=(variant&&variant.selling_plan_allocations)||[]; if(!allocs.length) return null;
  var best=null,bestD=1;
  allocs.forEach(function(a){
    if(!variant.price) return;
    var disc=1-(allocPerMonth(a,variant,target,months)/variant.price), d=Math.abs(disc-target);
    if(d<bestD){ bestD=d; best=a; }
  });
  return best;
}
/* Plan → selling-plan target discount. Each line is quantity 1:
   - one-time (target 0): no selling_plan.
   - monthly (0.10): the "deliver every month, 10% off" plan; Shopify bills monthly.
   - 3-month (0.25): a "deliver every 3 months" plan CUSTOM-PRICED at base×3×0.75
     (e.g. $75 pack → $168.75). Shopify charges that full amount upfront on a
     qty-1 order, which OK Capsule auto-fulfils as month 1; months 2-3 are placed
     manually as $0 duplicate orders (see Capsi-Project-Docs SOP). Do NOT send
     qty 3 — OK Capsule's tier caps orders at 30 pouches, and true prepaid selling
     plans are avoided because their expanded initial order breaks that cap too. */
/* Bring the REAL Shopify cart in line with this person's on-page selection. We only
   add/remove THIS session's own lines; earlier profiles' lines (snapshotted at load)
   are left alone. Serialised via syncChain so rapid clicks can't race.
   NOTE on pouch naming: OK Capsule prints ONE name per order — the order's consumer
   First Name (set at Shopify checkout) — and does NOT read line-item properties, so
   we deliberately attach no per-line name (it would be ignored and misleading). */
function syncCart(){
  if(!baselineSnapped) return Promise.resolve();   /* never write before snapshotting earlier profiles */
  syncChain = syncChain.then(reconcileCart, reconcileCart);  /* keep going even past a failed op */
  var cartSettled = syncChain;
  /* once the real cart matches the selection, replace the estimated shipping with
     the rate Shopify quotes for that cart (see refreshShippingRate). Queued on the
     chain so probes stay ordered, but NOT awaited by callers — checkout shouldn't
     wait on a display-only lookup. */
  syncChain = syncChain.then(refreshShippingRate, refreshShippingRate);
  return cartSettled;
}
function reconcileCart(){
  var planNow = plan;
  var target = plan==='3mo'?0.25:(plan==='mo'?0.10:0);
  var desired = Object.keys(cart).filter(function(s){ return cart[s]; });
  var ops = Promise.resolve();
  /* drop our lines that are no longer wanted; lines whose plan changed are
     switched IN PLACE (cart/change.js) rather than removed+re-added — a re-add
     fires Shopify's product_added_to_cart, which the Meta pixel counts as a
     fresh AddToCart every time the person flips between plan options */
  Object.keys(ourLineBySlug).forEach(function(s){
    var rec = ourLineBySlug[s];
    if(desired.indexOf(s)<0){ ops = ops.then(function(){ return removeOurLine(s); }); }
    else if(rec.plan!==planNow){ ops = ops.then(function(){ return changeOurLinePlan(s, target, planNow); }); }
  });
  /* add wanted lines we don't already own on the current plan */
  desired.forEach(function(s){
    ops = ops.then(function(){
      var rec = ourLineBySlug[s];
      if(rec && rec.plan===planNow) return;          /* already in the cart on the right plan */
      return addOurLine(s, target, planNow);
    });
  });
  return ops.then(saveSessionLines);               /* remember our lines so a refresh adopts them, not re-adds */
}
function addOurLine(slug, target, planNow){
  var prod=PRODUCTS[slug];
  if(!prod||!prod.handle){                            /* pack not wired to a product yet (dev/placeholder) */
    if(window.console) console.warn('[Capsi results] "'+slug+'" has no matching product in the "packs" collection, so it can\'t be added to the cart yet.');
    return Promise.resolve();
  }
  return fetchProduct(prod.handle).then(function(p){
    if(!p||!p.variants||!p.variants.length) return;
    var variant=p.variants[0];
    var item={id:variant.id, quantity:1};
    if(target>0){
      var months=(planNow==='3mo')?3:1;
      var a=pickAllocation(variant,target,months);
      if(a){
        item.selling_plan=a.selling_plan_id;
        /* the page shows a fixed 10%/25% discount; warn if the real plan differs,
           so a mis-configured product is caught before the total drifts at checkout */
        var disc=variant.price?(1-(allocPerMonth(a,variant,target,months)/variant.price)):0;
        if(window.console && Math.abs(disc-target)>0.03) console.warn('[Capsi results] "'+slug+'": nearest selling plan is '+Math.round(disc*100)+'% off but the page shows '+Math.round(target*100)+'% — displayed total may not match the cart.');
      } else if(window.console){
        console.warn('[Capsi results] "'+slug+'": no subscription selling plan on the product; adding it one-time. The page shows a subscription price, so the totals will not match.');
      }
    }
    return fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json','X-Requested-With':'XMLHttpRequest'},body:JSON.stringify({items:[item]})})
      .then(function(r){ return r.ok?r.json():null; })
      .then(function(res){ var line=res&&res.items&&res.items[0]; if(line) ourLineBySlug[slug]={key:line.key, plan:planNow}; });
  });
}
function removeOurLine(slug){
  var rec=ourLineBySlug[slug]; if(!rec) return Promise.resolve();
  var floor = baselineQtyByKey[rec.key]||0;           /* if an earlier profile shares this line, keep their qty */
  return fetch('/cart/change.js',{method:'POST',headers:{'Content-Type':'application/json','X-Requested-With':'XMLHttpRequest'},body:JSON.stringify({id:rec.key, quantity:floor})})
    .then(function(){ delete ourLineBySlug[slug]; }, function(){ delete ourLineBySlug[slug]; });
}
/* Switch an existing line's selling plan in place. cart/change.js fires no
   product_added_to_cart, so plan flips stop producing duplicate AddToCart
   pixel events. Falls back to remove+re-add when the line is shared with an
   earlier profile (changing it would drag their packs onto the new plan),
   when the product has no matching plan, or if Shopify rejects the change. */
function changeOurLinePlan(slug, target, planNow){
  var rec = ourLineBySlug[slug];
  var reAdd = function(){ return removeOurLine(slug).then(function(){ return addOurLine(slug, target, planNow); }); };
  if(!rec || (baselineQtyByKey[rec.key]||0) > 0) return reAdd();
  var prod = PRODUCTS[slug];
  if(!prod || !prod.handle) return reAdd();
  return fetchProduct(prod.handle).then(function(p){
    if(!p || !p.variants || !p.variants.length) return reAdd();
    var variant = p.variants[0];
    var planId = null;                                 /* null clears the plan (one-time) */
    if(target > 0){
      var a = pickAllocation(variant, target, (planNow==='3mo')?3:1);
      if(!a) return reAdd();                           /* no subscription plan: reAdd warns about it */
      planId = a.selling_plan_id;
    }
    return fetch('/cart/change.js',{method:'POST',headers:{'Content-Type':'application/json','X-Requested-With':'XMLHttpRequest'},body:JSON.stringify({id:rec.key, quantity:1, selling_plan:planId})})
      .then(function(r){ if(!r.ok) throw 0; return r.json(); })
      .then(function(c){
        var line=(c&&c.items||[]).filter(function(it){
          if(it.variant_id!==variant.id) return false;
          var sp=it.selling_plan_allocation&&it.selling_plan_allocation.selling_plan;
          return planId ? (sp&&sp.id===planId) : !sp;
        })[0];
        if(!line) throw 0;
        ourLineBySlug[slug]={key:line.key, plan:planNow};
      })
      .catch(reAdd);
  });
}
/* Remember the current person in the committed-profiles list (name + pack count)
   so the next person's results page can show who's already in the shared order. */
function rememberProfile(){
  var selected=[heroSlug].concat(addonSlugs).filter(function(s){return cart[s];});
  if(!selected.length) return;
  var list=getProfiles();
  list.push({ name:firstName||'', count:selected.length, packs:selected.map(function(s){return pack(s).name;}) });
  setProfiles(list);
  try{ localStorage.setItem(PLAN_KEY, plan); }catch(e){}   // first profile sets the order's cadence
}
/* Finds (or creates) the small inline error line under a given checkout button,
   so a failed sync is explained right where the click happened rather than
   silently redirecting to an empty cart. Styled inline since there's no
   existing "form error" class in this stylesheet to reuse. */
function checkoutErrEl(btn){
  var err=btn.nextElementSibling;
  if(!err || !err.hasAttribute('data-checkout-err')){
    err=document.createElement('p');
    err.setAttribute('data-checkout-err','');
    err.style.cssText='color:'+CORAL+';font-size:12.5px;margin-top:8px;text-align:center';
    err.hidden=true;
    btn.parentNode.insertBefore(err, btn.nextSibling);
  }
  return err;
}
/* Checkout: the selection is already live in the shared cart (syncCart), so just
   make sure any in-flight write has landed, record the profile, and go. The final
   syncCart() also self-heals — if an earlier write failed, it adds the missing
   lines before redirecting, so checkout never proceeds with a short cart.
   Belt-and-braces: even after syncCart settles, confirm against the REAL cart
   (fetch /cart.js) before redirecting — a pack whose product isn't wired yet
   (addOurLine no-ops with a console.warn) would otherwise sail through to an
   empty checkout with no explanation. */
function checkout(btn){
  if(monthlyBase()===0) return;
  var err = btn ? checkoutErrEl(btn) : null;
  if(err) err.hidden=true;
  if(btn){ btn.disabled=true; }
  syncCart().then(function(){
    /* A/B test 2026-09-27: stamp the order with the results-page variant it came from */
    return fetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json','X-Requested-With':'XMLHttpRequest'},
        body:JSON.stringify({attributes:{results_variant:root.getAttribute('data-results-variant')||'b'}})})
      .catch(function(){}).then(function(){
        return fetch('/cart.js',{headers:{'X-Requested-With':'XMLHttpRequest'}})
          .then(function(r){ return r.ok?r.json():null; });
      });
  }).then(function(c){
    if(!c || !c.item_count){
      // syncCart resolved but the real cart is still empty — most likely the
      // selected pack(s) have no matching product yet. Don't redirect into a
      // blank checkout; tell them and let them retry.
      if(btn) btn.disabled=false;
      if(err){ err.textContent="We couldn't add your packs to the cart. Please try again or contact us."; err.hidden=false; }
      return;
    }
    rememberProfile();
    window.location.href = foFor(plan) ? '/checkout?discount='+encodeURIComponent(FO_CODE) : '/checkout';
  }).catch(function(){
    if(btn) btn.disabled=false;
    if(err){ err.textContent="We couldn't add your packs to the cart. Please try again or contact us."; err.hidden=false; }
  });
}
/* "Add a second profile": the current person is already in the shared cart, so
   record them and start a fresh quiz for the next person WITHOUT wiping the order
   so far. Stashes the buyer email so the next quiz can reuse it. */
function addSecondProfile(){
  var btn=root.querySelector('[data-add-profile]');
  try{ localStorage.setItem('capsi_quiz_buyer_email', (data&&data.email)||''); }catch(e){}
  var next=function(){ window.location.href='/pages/quiz?addprofile=1'; };
  syncCart().then(function(){
    rememberProfile();
    /* this person's lines are now part of the committed order; the next profile
       must treat them as baseline, so stop tracking them as "ours" */
    ourLineBySlug={}; saveSessionLines();
    if(btn) btn.textContent='Saved ✓ · starting their assessment';
    setTimeout(next, 700);
  }).catch(next);
}

/* ---------------- hydrate real prices for shown packs, then render ---------------- */
function hydratePrices(){
  /* prices already resolve via basePrice() from the inline product map; fetch
     product.js for shown packs only to confirm availability/real price detail. */
  var shown=[heroSlug].concat(addonSlugs);
  var jobs=shown.map(function(s){
    var prod=PRODUCTS[s];
    if(!prod||!prod.handle) return Promise.resolve();
    return fetchProduct(prod.handle).then(function(p){
      if(p&&p.price){ PRODUCTS[s].price=p.price; }
    });
  });
  return Promise.all(jobs);
}

/* ---------------- init ---------------- */
personalise();
renderTimeline();
renderAll();
rtrack('results_view');
if(window.__capsiWatch){
  window.__capsiWatch(root.querySelector('[data-guarantee]'),'results_guarantee_view');
  window.__capsiWatch(root.querySelector('[data-why]'),'results_why_view');
}
/* crossing the desktop/mobile breakpoint swaps which summary is visible — fill
   the newly-active one (renderSummary only writes the visible target). */
if (window.matchMedia){
  var _sumMq = window.matchMedia('(max-width:920px)');
  var _onSumMq = function(){ renderSummary(); };
  if (_sumMq.addEventListener) _sumMq.addEventListener('change', _onSumMq);
  else if (_sumMq.addListener) _sumMq.addListener(_onSumMq);
}
/* Load = read only. The hero is pre-selected ON THE PAGE, but nothing is written to
   the Shopify cart until the person acts (toggle, plan change, checkout, add a
   profile). Syncing here used to POST /cart/add.js on every results view, which
   fired product_added_to_cart -> Meta AddToCart for people who never chose
   anything, and re-added a pack they had just removed in the drawer. checkout()
   still runs syncCart() first, so the selection always lands before redirecting. */
loadCommitted().then(function(){ renderCommitted(); renderAll(); });
hydratePrices().then(renderAll);
})();
